/**
 * Street Dice Legends - Client Entry Point
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { Engine } from './engine/Engine';
import { sound } from './engine/audio/sound';
import { installUiClickSounds } from './ui/uiClickSounds';
import { SharedRenderer } from './engine/render/SharedRenderer';
import { AssetLoader } from './engine/loaders/AssetLoader';
import { prewarmObjectVisibility } from './engine/render/Prewarm';
import { DiceController } from './game/dice/DiceController';
import { TutorialController } from './game/tutorial/TutorialController';
import { GameStateManager, GameScene, CameraController, GameFlowController } from './game';
import { UIManager, HomeScreen, DiceScreen, CrewScreen, StoreScreen, LobbyScreen, NotificationsScreen, LoginScreen, SettingsScreen, PlayerProfilesOverlay, SidebetButtonsOverlay, CancelBetOverlay, CountdownOverlay, InstructionBanner, InGameMenu } from './ui';
import { InGameChat } from './ui/components/chat/InGameChat';
import { VoiceClient } from './network/voice/VoiceClient';
import { settingsStore, type SettingsState } from './settings/SettingsStore';
import { LobbyService } from './services/LobbyService';
import { CreateRoomModal } from './ui/components/lobby/CreateRoomModal';
import { LoadingScreen } from './ui/LoadingScreen';
import { uuid } from './util/uuid';
import { BettingController, BettingHUD3D } from './game/betting';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { NetworkManager } from './network/NetworkManager';
import { serverConfig } from './network/ServerConfig';
import { DiceAssetManager } from './game/dice/assets/DiceAssetManager';
import type { AuthSuccessPayload, DiceGearUpdatedPayload, NotificationListPayload, GameNotification } from '@sdl/shared';
import { ChatService } from './services/ChatService';
import { OfferService } from './services/OfferService';
import { OfferPopup } from './ui/components/OfferPopup';
import { createAdProvider } from './iap/AdProvider';
import type { OfferWire } from '@sdl/shared';
import { BlockService } from './services/BlockService';
import { ChatNotificationBubble } from './ui/components/ChatNotificationBubble';
import { StandaloneChat } from './ui/components/crew/StandaloneChat';
import { PlayerCardController } from './ui/components/crew/PlayerCardController';
import { AvatarUploadController } from './ui/components/AvatarUploadController';
import { BonusSuitcaseOverlay } from './ui/components/BonusSuitcaseOverlay';
import { CoachMarkOverlay } from './ui/components/CoachMarkOverlay';
import { preloadCanvasFonts } from './ui/fontPreload';
import { showToast } from './ui/components/Toast';
import { WelcomeModal } from './ui/components/WelcomeModal';
import { CashRewardOverlay } from './ui/components/CashRewardOverlay';
import { IapManager } from './iap/IapManager';
import { notificationService } from './services/NotificationService';
import { NotificationPrimer } from './ui/components/NotificationPrimer';
import { ratingService } from './services/RatingService';
import { RatingPrimer } from './ui/components/RatingPrimer';
import type { IapGranted, IapBalance } from './api/iapApi';
import { ComingSoonOverlay, type ComingSoonOptions } from './ui/components/ComingSoonOverlay';
import { UsernamePromptModal } from './ui/components/UsernamePromptModal';
import { ErrorModal } from './ui/components/ErrorModal';
import { ConfirmModal } from './ui/components/ConfirmModal';
import { googleSignIn, googleSignOut } from './auth/googleSignIn';
import { appleSignIn } from './auth/appleSignIn';
import { DebouncedUsernameChecker } from './utils/DebouncedUsernameChecker';
import { getNoteCdnUrl, fetchCurrencyNotes } from './api/currencyNoteApi';
import type { DiceItem } from './ui/screens/DiceScreen';
import { FlyoutDebugPanel } from './ui/components/FlyoutDebugPanel';

// Physics debug visualization
let physicsDebugEnabled = false;
const physicsDebugMeshes: THREE.LineSegments[] = [];

function updatePhysicsDebug(physicsWorld: any, scene: THREE.Scene): void {
  if (!physicsDebugEnabled) {
    // Remove existing debug meshes
    for (const mesh of physicsDebugMeshes) {
      scene.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    physicsDebugMeshes.length = 0;
    return;
  }

  const world = physicsWorld.world as CANNON.World;
  const bodies = world.bodies;

  // Remove old meshes
  for (const mesh of physicsDebugMeshes) {
    scene.remove(mesh);
    mesh.geometry.dispose();
    (mesh.material as THREE.Material).dispose();
  }
  physicsDebugMeshes.length = 0;

  // Create wireframes for each body
  for (const body of bodies) {
    for (const shape of body.shapes) {
      let geometry: THREE.BufferGeometry | null = null;

      if (shape instanceof CANNON.Box) {
        const halfExtents = shape.halfExtents;
        geometry = new THREE.BoxGeometry(
          halfExtents.x * 2,
          halfExtents.y * 2,
          halfExtents.z * 2
        );
      } else if (shape instanceof CANNON.Sphere) {
        geometry = new THREE.SphereGeometry(shape.radius, 8, 6);
      } else if (shape instanceof CANNON.Plane) {
        geometry = new THREE.PlaneGeometry(20, 20);
      }

      if (geometry) {
        const edges = new THREE.EdgesGeometry(geometry);
        const material = new THREE.LineBasicMaterial({
          color: body.mass > 0 ? 0x00ff00 : 0x0088ff,
          depthTest: false,
          transparent: true,
          opacity: 0.7,
        });
        const wireframe = new THREE.LineSegments(edges, material);
        wireframe.position.copy(body.position as any);
        wireframe.quaternion.copy(body.quaternion as any);
        wireframe.renderOrder = 999;
        scene.add(wireframe);
        physicsDebugMeshes.push(wireframe);
        geometry.dispose();
      }
    }
  }
}

// Expose toggle function to window for debugging
(window as any).togglePhysicsDebug = () => {
  physicsDebugEnabled = !physicsDebugEnabled;
  console.log(`[Debug] Physics debug ${physicsDebugEnabled ? 'enabled' : 'disabled'}`);
};
(window as any).showDiceInfo = () => {
  if (diceController) {
    const dice = diceController.getDice();
    console.log('[Debug] Dice info:', {
      count: dice.length,
      positions: dice.map(d => d.mesh.position.toArray()),
      inScene: dice.map(d => d.mesh.parent !== null),
    });
  } else {
    console.log('[Debug] No dice controller');
  }
};


// Global instances
let engine: Engine | null = null;
let uiManager: UIManager | null = null;
let gameStateManager: GameStateManager | null = null;
let gameScene: GameScene | null = null;
let diceController: DiceController | null = null;
let cameraController: CameraController | null = null;
let homeScreen: HomeScreen | null = null;
let networkManager: NetworkManager | null = null;
let gameFlowController: GameFlowController | null = null;
let bettingController: BettingController | null = null;
let bettingHUD3D: BettingHUD3D | null = null;
let bettingAssetLoader: AssetLoader | null = null;
let playerProfilesOverlay: PlayerProfilesOverlay | null = null;
let countdownOverlay: CountdownOverlay | null = null;
let sidebetButtonsOverlay: SidebetButtonsOverlay | null = null;
let cancelBetOverlay: CancelBetOverlay | null = null;
let instructionBanner: InstructionBanner | null = null;
let inGameMenu: InGameMenu | null = null;
let inGameChat: InGameChat | null = null;
let voiceClient: VoiceClient | null = null;
let diceScreen: DiceScreen | null = null;
let crewScreen: CrewScreen | null = null;
let notificationsScreen: NotificationsScreen | null = null;
let settingsScreen: SettingsScreen | null = null;
let shopScreen: StoreScreen | null = null;
let iapManager: IapManager | null = null;
let playerCardController: PlayerCardController | null = null;
let avatarUploadController: AvatarUploadController | null = null;
/** Local player's id — set on auth, used to skip self-taps on player cards. */
let localPlayerId: string | null = null;
/** JWT from auth:success — needed for authenticated HTTP calls (avatar upload). */
let authToken: string | null = null;
let chatService: ChatService | null = null;
/** Offer engine client cache (spec §8) — drives the StoreScreen tab + popup. */
let offerService: OfferService | null = null;
/** Full-screen tap-to-act offer popup (baked card image). */
let offerPopup: OfferPopup | null = null;
/** Latest live offer set + whether the startup popup has shown this session. */
let latestOffers: OfferWire[] = [];
let offerStartupShown = false;

/** Show the startup offer popup once per session, after the home screen is up. */
function maybeShowStartupOffers(): void {
  if (offerStartupShown || !loaderDismissed || !offerPopup) return;
  if (latestOffers.length === 0) return;
  offerStartupShown = true;
  offerPopup.show(latestOffers);
}
/** Source of truth for the local player's blocked list (lobby/chat/search/card). */
const blockService = new BlockService();
let createRoomModal: CreateRoomModal | null = null;
let welcomeModal: WelcomeModal | null = null;
/** Set on first auth when the tutorial isn't done; consumed AFTER the loading
 *  screen dismisses so the Welcome modal animates in over the loaded home
 *  screen, not on top of the loader. */
let pendingWelcomeGate = false;
/** True once the boot loading screen has been dismissed. After that, a late
 *  auth:success (e.g. first social sign-in waiting on the username prompt
 *  outlasting the loader's 8s auth timeout) must show the Welcome gate
 *  itself — the boot-time consumer in main() has already run. */
let loaderDismissed = false;
let tutorialController: TutorialController | null = null;
let lobbyService: LobbyService | null = null;
let lobbyScreen: LobbyScreen | null = null;
/** Latest known local-player balance, updated on auth + balance:update. */
let localBalance: { stacks: number; gold: number; diceTokens: number } = { stacks: 0, gold: 0, diceTokens: 0 };
/** Local-player display info (for the cash-reward card). */
let localUsername = '';
let localPicture = '';
/** Connect-account bonus state: is the account linked, and is 100M already claimed. */
let localLinked = false;
let localConnectBonusClaimed = false;
/** True once the one-time auth:success subsystem init has run. A second
 *  auth:success (e.g. a guest linking a social account in-app re-emits a full
 *  AUTH_SUCCESS) must refresh state WITHOUT re-creating ChatService/crew/dice. */
let authInitialized = false;
/** The Stacks balance the player has already been *shown* (the single gate that
 *  prevents a gain being celebrated twice). Seeded on auth; bumped only by
 *  presentBalanceGain after it animates. */
let lastSeenBalance = 0;
/** Pending social sign-in chosen at the login screen (emitted after connect). */
let pendingSocialAuthMain: { provider: 'google' | 'apple'; token: string; tokenType: 'access' | 'id' } | null = null;
let usernamePrompt: UsernamePromptModal | null = null;
const errorModal = new ErrorModal();
const confirmModal = new ConfirmModal();

/** Compact currency formatting for the store wallet pills (K/M/B/T). */
function formatCompact(n: number): string {
  if (n >= 1e12) return `${(n / 1e12).toFixed(n >= 1e13 ? 0 : 1)}T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(n >= 1e10 ? 0 : 1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}K`;
  return String(Math.floor(n));
}

/** Refresh the store-screen wallet pills from the latest local balance. */
function refreshShopWallet(): void {
  shopScreen?.updateWallet({
    stacks: formatCompact(localBalance.stacks),
    gold: localBalance.gold.toLocaleString('en-US'),
    diceTokens: String(localBalance.diceTokens),
  });
}

/** Gold-coin / dice-token note art for the IAP flyout (no CDN bill exists for
 *  these — reuse the same flyout with these icon notes). */
const GOLD_NOTE_URL = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="13" fill="#ffd24a" stroke="#c98a12" stroke-width="2.5"/><circle cx="16" cy="16" r="8" fill="none" stroke="#c98a12" stroke-width="1.6"/></svg>',
);
const TOKEN_NOTE_URL = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect x="4" y="4" width="24" height="24" rx="6" fill="#ff2e8a" stroke="#c00060" stroke-width="2"/><g fill="#fff"><circle cx="11" cy="11" r="2"/><circle cx="21" cy="11" r="2"/><circle cx="11" cy="21" r="2"/><circle cx="21" cy="21" r="2"/><circle cx="16" cy="16" r="2"/></g></svg>',
);

function pillCenter(r: DOMRect): { x: number; y: number } {
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/** Render a server-delivered IAP grant: fly cash / gold / tokens FROM the tapped
 *  bundle item TO their wallet pills using the same in-game cash flyout
 *  (flyoutCashWin), counting each pill up as the notes land. (Dice unlocks ride
 *  the dice-unlock modal once offers ship.) */
async function handleIapGranted(granted: IapGranted, balance: IapBalance, productId: string, isReal: boolean): Promise<void> {
  // Store-pill count-up endpoints. Real: server balance is the truth. Dev stub:
  // count from the current local value up by the grant (display-only).
  const startS = isReal
    ? { stacks: Math.max(0, balance.stacks - granted.stacks), gold: Math.max(0, balance.gold - granted.gold), diceTokens: Math.max(0, balance.diceTokens - granted.diceTokens) }
    : { stacks: localBalance.stacks, gold: localBalance.gold, diceTokens: localBalance.diceTokens };
  const endS = isReal
    ? { stacks: balance.stacks, gold: balance.gold, diceTokens: balance.diceTokens }
    : { stacks: localBalance.stacks + granted.stacks, gold: localBalance.gold + granted.gold, diceTokens: localBalance.diceTokens + granted.diceTokens };

  // REAL purchase only: adopt the server balance + mark the gain already-seen so
  // home doesn't replay its flyout. The dev stub must NOT touch these globals —
  // its fake balance would poison lastSeenBalance and break later real flyouts
  // (e.g. the daily bonus came out as a non-positive amount and got skipped).
  if (isReal) {
    localBalance = { stacks: balance.stacks, gold: balance.gold, diceTokens: balance.diceTokens };
    lastSeenBalance = balance.stacks;
  }

  const overlay = playerProfilesOverlay;
  const bundle = shopScreen?.getBundleRect(productId);
  const spawn = bundle
    ? { x: bundle.left + bundle.width / 2, y: bundle.top + bundle.height / 2 }
    : { x: window.innerWidth / 2, y: window.innerHeight * 0.6 };

  if (overlay && shopScreen) {
    overlay.setFlyoutAboveUI(true);
    overlay.show();

    // Cash → Stacks pill (real in-game bill art; default "+$X" label).
    if (granted.stacks > 0) {
      await fetchCurrencyNotes().catch(() => { /* fall back to icon */ });
      const noteUrl = getNoteCdnUrl(100_000_000) ?? getNoteCdnUrl(granted.stacks) ?? GOLD_NOTE_URL;
      const pill = shopScreen.getWalletPillRect('stacks');
      if (pill) {
        await overlay.flyoutCashWin('', granted.stacks, spawn, noteUrl,
          () => shopScreen?.countPillUp('stacks', startS.stacks, endS.stacks),
          { targetOverride: pillCenter(pill), skipBalanceCountUp: true });
      }
    }
    // Gold → Gold pill (coin note, "+N" label).
    if (granted.gold > 0) {
      const pill = shopScreen.getWalletPillRect('gold');
      if (pill) {
        // Gold count scales with the amount (default amount/500), capped.
        await overlay.flyoutCashWin('', granted.gold, spawn, GOLD_NOTE_URL,
          () => shopScreen?.countPillUp('gold', startS.gold, endS.gold),
          { targetOverride: pillCenter(pill), skipBalanceCountUp: true, labelText: `+${granted.gold.toLocaleString('en-US')}` });
      }
    }
    // Tokens → Token pill (token note, "+N" label).
    if (granted.diceTokens > 0) {
      const pill = shopScreen.getWalletPillRect('tokens');
      if (pill) {
        // 1 token = 1 icon (flyout caps at maxNotes).
        await overlay.flyoutCashWin('', granted.diceTokens, spawn, TOKEN_NOTE_URL,
          () => shopScreen?.countPillUp('tokens', startS.diceTokens, endS.diceTokens),
          { targetOverride: pillCenter(pill), skipBalanceCountUp: true, noteCountOverride: granted.diceTokens, labelText: `+${granted.diceTokens}` });
      }
    }

    // Let the last "+N" label linger before tearing down the overlay.
    await new Promise((r) => setTimeout(r, 700));
    overlay.hide();
    overlay.setFlyoutAboveUI(false);
  }

  if (isReal) {
    refreshShopWallet();
    // Keep home in sync for when the player navigates back.
    homeScreen?.updatePlayerData({ stacks: balance.stacks, gold: balance.gold });
  }

  if (granted.dice && granted.dice.length > 0) {
    // TODO(offers): multi-dice unlock modal with particle explosion.
    console.log('[iap] dice unlocked (modal pending):', granted.dice);
  }
}
let diceAssetManager: DiceAssetManager | null = null;
let diceCdnBase: string | null = null;
/** The dice-model catalog preload, captured so boot can wait for it before
 *  dismissing the loader (dice ready before the home screen). Null until auth. */
let modelsPreloadPromise: Promise<void> | null = null;
let chatBubble: ChatNotificationBubble | null = null;
let standaloneChat: StandaloneChat | null = null;
let loginScreen: LoginScreen | null = null;
let bonusSuitcaseOverlay: BonusSuitcaseOverlay | null = null;
/** One-time post-tutorial coach mark pointing at the daily-bonus gift button. */
let dailyBonusCoach: CoachMarkOverlay | null = null;
let pendingBonusPrize: number | null = null;
let pendingBonusMultiplier: number = 1;
/** Daily-bonus award awaiting the suitcase close → notes-to-pill animation. */
let pendingDailyAward: { amount: number; newBalance: number } | null = null;
let equipDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let authReady = false;
let pendingMatch = false;
let gameNetworkHandlersBound = false;
const loadingScreen = new LoadingScreen();
let sceneSetupResolve: (() => void) | null = null;
/** True between game:started and game:ended. Used by the resume handler
 *  to decide whether a foreground event should re-sync game state. */
let inGame = false;
/** Guard so overlapping visibility events don't stack resume flows. */
let resumeRefreshInFlight = false;

/**
 * Apply persisted user settings to the live engine subsystems. Idempotent —
 * safe to call on boot and again on every SettingsStore `change`.
 */
/**
 * Join the match voice mesh if voice chat is enabled and we're seated in a
 * game. Idempotent (VoiceClient.join guards re-entry), so it's safe to call
 * from the settings toggle and on every game:update.
 */
function maybeJoinVoice(): void {
  if (!voiceClient || !networkManager) return;
  if (!settingsStore.get('audio').voiceChatEnabled) return;
  if (!networkManager.isInGame()) return;
  void voiceClient.join();
}

function applySettings(state: SettingsState): void {
  if (engine) {
    const a = state.audio;
    engine.audio.setVolume('master', a.master);
    engine.audio.setVolume('music', a.music);
    engine.audio.setVolume('sfx', a.sfx);
    engine.audio.setVolume('ui', a.ui);
    engine.audio.setVolume('voice', a.voice);
    if (a.muted !== engine.audio.isMuted()) {
      if (a.muted) engine.audio.mute();
      else engine.audio.unmute();
    }
  }

  // Voice chat: incoming-voice playback level + master enable/disable. The
  // WebRTC peer audio plays through its own <audio> elements (independent of
  // the Howler 'voice' bus above), so the slider is applied to VoiceClient too.
  const av = state.audio;
  voiceClient?.setPlaybackVolume(av.voice);
  if (av.voiceChatEnabled) maybeJoinVoice();
  else voiceClient?.leave();
  try {
    SharedRenderer.instance.setQuality(state.graphics.quality);
    // When a game is live, also re-apply to the scene lights so a quality
    // change actually resizes the shadow map (SharedRenderer.setQuality only
    // sets pixel ratio). No-op on menu screens where the engine isn't running.
    engine?.setQuality(state.graphics.quality);
  } catch {
    // Renderer not initialized yet (called before SharedRenderer.init) — the
    // init() call already seeds quality from the store, so this is harmless.
  }
}

/**
 * Open the floating StandaloneChat with a peer. Used by incoming-message
 * bubbles and by player cards opened OUTSIDE the crew modal (lobby / in-game),
 * where the embedded crew chat isn't on screen. Player cards opened from the
 * crew modal use the crew screen's own embedded chat instead.
 */
function openStandaloneChat(peerId?: string, peerUsername?: string, peerPicture?: string): void {
  if (!chatService || !localPlayerId) return;
  const container = document.getElementById('ui-container');
  if (!container) return;

  if (standaloneChat) {
    standaloneChat.destroy();
    standaloneChat = null;
  }
  standaloneChat = new StandaloneChat({
    container,
    chatService,
    localPlayerId,
    peerId,
    peerUsername,
    peerPicture,
    resolvePeer: (id) => crewScreen?.getFriend(id),
  });
  standaloneChat.on('close', () => {
    standaloneChat = null;
  });
  // Tap the floating-chat header avatar → open that peer's player card.
  standaloneChat.on('profile:tap', ({ playerId, username, picture }) => {
    if (!playerCardController || playerId === localPlayerId) return;
    playerCardController.show({ id: playerId, username: username ?? 'Player', picture });
  });
}

/**
 * Show login screen and wait for player to choose auth method
 */
function showLoginScreen(container: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    loginScreen = new LoginScreen();
    container.appendChild(loginScreen.element);

    loginScreen.show();

    const proceed = () => {
      // Show loading screen, hide login behind it, then continue initialization
      loadingScreen.show();
      loginScreen?.hide();
      setTimeout(() => {
        loginScreen?.destroy();
        loginScreen = null;
        resolve();
      }, 400);
    };

    loginScreen.on('guest-login', () => {
      const token = uuid();
      localStorage.setItem('sdl_auth_token', token);
      localStorage.setItem('sdl_auth_type', 'guest');
      proceed();
    });

    const socialLogin = async (provider: 'google' | 'apple') => {
      let result;
      try {
        result = provider === 'google' ? await googleSignIn() : await appleSignIn();
      } catch (e) {
        console.error(`[${provider}-login] sign-in error`, e);
        const name = provider === 'google' ? 'Google' : 'Apple';
        errorModal.show('Sign-in failed', `Couldn’t sign in with ${name}. Please try again.`);
        return;
      }
      if (!result) return; // user cancelled — stay on login screen
      // Ensure a persistent device token so AUTH_GUEST re-auths the (soon to be
      // linked) account on the next launch.
      if (!localStorage.getItem('sdl_auth_token')) {
        localStorage.setItem('sdl_auth_token', uuid());
        localStorage.setItem('sdl_auth_type', provider);
      }
      pendingSocialAuthMain = { provider, token: result.token, tokenType: result.tokenType };
      proceed();
    };
    loginScreen.on('google-login', () => void socialLogin('google'));
    loginScreen.on('apple-login', () => void socialLogin('apple'));
  });
}

async function main() {
  console.log('🎲 Starting Street Dice Legends...');

  // Pull canvas-drawn fonts (Oswald et al.) into the font cache up front so the
  // first TSS/HUD label draw uses the real face instead of swapping in late.
  void preloadCanvasFonts();

  // Setup fullscreen immersive mode on native platforms
  if (Capacitor.isNativePlatform()) {
    try {
      await StatusBar.hide();
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setOverlaysWebView({ overlay: true });
    } catch (e) {
      console.warn('StatusBar plugin not available:', e);
    }
  }

  // Initialize the shared WebGL renderer (single context for the whole app).
  // Honor the user's saved graphics quality so older phones boot at their
  // chosen tier instead of always starting at 'high'.
  SharedRenderer.init({ quality: settingsStore.get('graphics').quality });

  // Get containers
  const gameContainer = document.getElementById('game-container');
  const uiContainer = document.getElementById('ui-container');

  if (!gameContainer || !uiContainer) {
    throw new Error('Required containers not found');
  }

  // Hide game container initially (we start on the menu)
  gameContainer.style.visibility = 'hidden';

  // Check for existing auth token
  const hasAuthToken = !!localStorage.getItem('sdl_auth_token');

  if (!hasAuthToken) {
    // No token — hide initial loading screen, show login, wait for guest choice
    await loadingScreen.hide();
    await showLoginScreen(uiContainer);
  }

  // Initialize engine
  engine = new Engine({
    container: gameContainer,
    renderer: {
      quality: settingsStore.get('graphics').quality,
      shadows: true,
    },
    physics: {
      gravity: 30,
    },
  });

  // Wire the global sound facade to the engine's AudioManager and preload the
  // sound catalog. No-op + silent until SOUND_ASSETS_READY flips (assets TBD),
  // so the triggers wired through the UI/game are harmless until then.
  sound.init(engine.audio);

  // Give every clickable UI element a default click sound (from the active
  // GameTheme). One global delegated listener; per-element override/opt-out via
  // data-sound. Silent-safe until the catalog is ready.
  installUiClickSounds();

  // Push saved settings into the live subsystems now that engine + renderer
  // exist, then keep them in sync as the user changes them in Settings.
  applySettings(settingsStore.getAll());
  settingsStore.on('change', applySettings);

  // Initialize game state manager
  gameStateManager = new GameStateManager(engine);

  // Initialize UI
  uiManager = initializeUI(uiContainer);

  // When home becomes visible via tab nav, recap any un-shown balance gain
  // (delayed so the slide settles before the flyout lands on the pill).
  uiManager.on('screen:change', (data) => {
    const to = (data as { to?: string } | undefined)?.to;
    if (to === 'home') setTimeout(() => checkHomeBalanceGain(), 450);
  });

  // Flyout debug panel (Ctrl+Shift+F to toggle)
  new FlyoutDebugPanel(document.body);

  // Initialize network
  networkManager = new NetworkManager();

  // Body-level player card shared by lobby + in-game profile taps. Built here
  // (not in initializeUI) because it needs networkManager, which is created
  // after the UI. crewScreen + uiManager already exist by now.
  if (crewScreen) {
    const crew = crewScreen;
    playerCardController = new PlayerCardController({
      networkManager,
      blockService,
      lookupFriend: (id) => crew.getFriend(id),
      // Lobby / in-game cards open the floating standalone chat — the crew
      // modal isn't the active surface there. (Cards opened inside the crew
      // modal go through CrewScreen.showPlayerCard → embedded chat.)
      onChat: (id, username) => openStandaloneChat(id, username, crew.getFriend(id)?.picture),
    });
  }

  // Blocked list: server pushes the enriched list on auth and after every
  // block/unblock. BlockService fans changes out to the lobby (hide presence +
  // chat), the Settings manager, and the player card.
  networkManager.on('blocked:list', (data) => blockService.setAll(data.blocked));
  networkManager.on('friend:unblocked', (data) => blockService.remove(data.playerId));
  blockService.on('changed', ({ blocked }) => {
    lobbyService?.setBlockedIds(blockService.ids());
    settingsScreen?.setBlocked(blocked);
  });

  networkManager.on('auth:success', (data: AuthSuccessPayload) => {
    console.log('[BOOT] 3. auth:success handler START');
    authReady = true;
    authToken = data.token;
    const { player } = data;
    localPlayerId = player.id;
    localBalance = { stacks: player.balance.stacks, gold: player.balance.gold, diceTokens: (player.balance as { diceTokens?: number }).diceTokens ?? 0 };
    localUsername = player.username;
    localPicture = (player as { picture?: string }).picture ?? '';
    localLinked = !!player.socialProvider;
    localConnectBonusClaimed = player.connectBonus?.claimed ?? false;
    // Seed the "seen" gate so the initial load doesn't animate the starting balance.
    lastSeenBalance = player.balance.stacks;
    // Tell the lobby who "you" are so chat bubbles align right when you speak.
    lobbyScreen?.setLocalUser({
      id: player.id,
      username: player.username,
      ringColor: 'gold',
    });
    // Populate the Settings → Account tab. Guest detection uses the server's
    // `Player####` naming until email/isGuest are added to the auth payload.
    // socialProvider is authoritative now that it's in the auth payload — a
    // guest is simply an account with no linked provider.
    settingsScreen?.setAccount({
      username: player.username,
      picture: (player as { picture?: string }).picture ?? '',
      isGuest: !player.socialProvider,
    });
    if (!homeScreen) return;
    homeScreen.updateFromServer(player);
    console.log('[BOOT] 4. home screen populated from server');

    // Re-entry (a guest just linked an account in Settings): the heavy
    // subsystem init below already ran on the first auth. Just refresh state
    // and claim the 100M connect bonus — don't rebuild chat/crew/dice.
    if (authInitialized) {
      triggerConnectBonus();
      return;
    }
    authInitialized = true;

    // Push notifications. Wire deps, then SILENTLY register the token if the OS
    // permission is already granted. This NEVER prompts — the OS prompt only
    // fires later from the soft-ask flow (maybePrimeNotifications). onDeepLink
    // routes a tapped push to the right surface.
    notificationService.init({
      registerToken: (token, platform) => networkManager?.registerPushToken(token, platform),
      unregisterToken: (token) => networkManager?.unregisterPushToken(token),
      onDeepLink: (url) => routePushDeepLink(url),
    });
    void notificationService.syncIfAuthorized();

    // Start the rating age clock from first launch (idempotent).
    ensureRatingFirstSeen();

    // Populate DiceScreen from server gear data
    if (diceScreen && player.gear) {
      const { gear } = player;
      const items: DiceItem[] = gear.ownedDice.map(id => ({
        id,
        thumbnailUrl: `${gear.cdnBase}/dice/${id}/thumb-128.png`,
      }));
      diceScreen.setOwnedDice(items);
      diceScreen.setPresets(gear.presets);
      diceScreen.setActivePreset(gear.activePreset);

      // Create DiceAssetManager and preload ALL dice models at startup
      // (PhoneDice pattern: cache every GLB so cloning is instant during gameplay)
      if (gear.cdnBase) {
        diceCdnBase = gear.cdnBase;
        diceAssetManager = new DiceAssetManager({
          cdnBase: gear.cdnBase,
          apiBaseUrl: serverConfig.mainServerUrl,
        });
        // Preload all base GLB models. Captured so boot waits for it before
        // dismissing the loader — the dice must be ready before the home screen.
        modelsPreloadPromise = diceAssetManager.preloadAllModels().catch((err) => {
          console.warn('[main.ts] Dice model catalog preload failed:', err);
        });
        // Also preload equipped dice documents (skin data — lightweight JSON)
        const equippedIds = gear.presets[gear.activePreset]?.slots.filter(Boolean) as string[];
        if (equippedIds.length > 0) {
          diceAssetManager.preload(equippedIds).catch((err) => {
            console.warn('[main.ts] Dice asset preload failed:', err);
          });
        }
      }
    }

    // Offer engine: cache the live set the server pushed after auth, feed the
    // StoreScreen OFFERS tab, and surface the startup popup once the home screen
    // is up (free + video first, then the single random paid card).
    if (networkManager && !offerService) {
      offerService = new OfferService(networkManager);
      offerService.on('offers:changed', (offers) => {
        latestOffers = offers;
        shopScreen?.setOffers(offers);
        maybeShowStartupOffers();
      });
      offerService.on('offer:claim_result', (result) => {
        offerPopup?.notifyClaimResult(result);
      });
    }

    // Initialize Crew system
    if (crewScreen && networkManager) {
      chatService = new ChatService(networkManager, data.playerId);

      // The server sends only friend IDs on auth, then a separate enriched
      // `friend:list` event (handled in CrewScreen) with names/pictures/balances.
      // Start empty; the list event populates the crew tabs moments later.
      const favouriteIds = player.favourites ?? [];

      crewScreen.initialize({
        playerId: data.playerId,
        friends: [],
        favourites: favouriteIds,
        chatService,
        networkManager,
        blockService,
      });

      // Create notification bubble
      const bubbleContainer = document.getElementById('ui-container');
      if (bubbleContainer) {
        chatBubble = new ChatNotificationBubble({ container: bubbleContainer });

        // Show bubble when a message arrives and chat isn't already open
        chatService.on('message:received', async (msg) => {
          if (!chatService) return;
          // First time a DM lands from someone else is a concrete value moment —
          // prime notifications so they don't miss the next one. One-shot, gated.
          if (msg.from !== data.playerId) void maybePrimeNotifications();
          const isCrewVisible = uiManager?.getCurrentScreen() === 'crew';
          const isChatOpen = standaloneChat !== null;
          if (!isCrewVisible && !isChatOpen && msg.from !== data.playerId) {
            const count = chatService.unreadCount;
            chatBubble?.show({
              peerId: msg.from,
              peerUsername: msg.fromUsername,
              picture: msg.fromPicture,
              unreadCount: count,
            });
          }
        });

        // Tap bubble -> open standalone chat (shared opener)
        chatBubble.on('bubble:tap', ({ peerId }) => {
          chatBubble?.hide();
          openStandaloneChat(peerId);
        });
      }

      // Update nav badge from unread count
      chatService.on('unread:changed', ({ count: _count }: { count: number }) => {
        // TODO: UIManager dynamic badge API
        chatBubble?.updateBadge(_count);
      });
    }

    // Initialize notifications screen
    if (notificationsScreen && networkManager) {
      notificationsScreen.initialize(networkManager);
    }

    console.log('[BOOT] 5. subsystem init (dice/crew/notifications) done');

    // Request home screen data (bonus availability, etc.)
    networkManager?.requestHomeData();

    // First-login tutorial gate. `tutorial` is server-authoritative state on
    // the player; if it's not yet completed, queue the Welcome modal. It's
    // shown only after loadingScreen.hide() resolves (see main()), so it
    // animates in over the loaded home screen instead of over the loader.
    if (player.tutorial && !player.tutorial.completed) {
      pendingWelcomeGate = true;
      // Loader already gone (auth outlasted its 8s timeout — happens when the
      // first social sign-in sits on the username prompt) → show it now.
      if (loaderDismissed) {
        pendingWelcomeGate = false;
        showWelcomeGate();
      }
    }

    if (pendingMatch) {
      pendingMatch = false;
      networkManager?.findMatch({ gameType: 'ceelo', stakes: 'low' });
    }
    console.log('[BOOT] 6. auth:success handler END');
  });

  // Home screen data (bonus availability)
  networkManager.on('home:data', (data) => {
    const { dailyBonus } = data;
    pendingBonusPrize = dailyBonus.prize;
    pendingBonusMultiplier = dailyBonus.multiplier;
    homeScreen?.updatePlayerData({
      hasDailyBonus: dailyBonus.available,
      bonusStreak: dailyBonus.streak,
      bonusMultiplier: dailyBonus.multiplier,
      nextBonusAt: dailyBonus.nextAvailableAt ?? null,
    });
  });

  // Daily bonus claim result — stash the award; the notes-to-pill animation
  // plays when the suitcase closes (the count-up rides that, not this event).
  networkManager.on('daily_bonus:result', (data) => {
    if (data.success) {
      homeScreen?.updatePlayerData({ bonusStreak: data.streak, bonusMultiplier: data.multiplier });
      pendingDailyAward = { amount: data.amount, newBalance: data.newBalance };
      // Note: the notification soft-ask is NOT fired here. It rides the END of
      // the cash flyout (suitcase 'close' → presentBalanceGain) so the primer
      // lands cleanly on the home screen after the money animation, not on top
      // of it.
    }
  });

  // Balance update from server (gifts, game payouts, etc.)
  networkManager.on('balance:update', (data) => {
    localBalance = { stacks: data.stacks, gold: data.gold, diceTokens: data.diceTokens ?? localBalance.diceTokens };
    createRoomModal?.setBalance(data.stacks);
    refreshShopWallet();
    const onHome = uiManager?.getCurrentScreen() === 'home';
    // A positive gain landing while we're on home — e.g. game winnings whose
    // settlement `balance:update` arrives AFTER we've returned to the lobby (so
    // checkHomeBalanceGain's one-shot check already passed) — gets the same
    // flyout the bonuses do. The lastSeenBalance gate dedupes against
    // checkHomeBalanceGain so it animates exactly once. Otherwise snap the pill.
    if (onHome && !bonusInFlight && data.stacks > lastSeenBalance) {
      void presentBalanceGain(data.stacks);
    } else if (bonusInFlight) {
      // A cash-award animation owns the Stacks pill — don't snap it (let the
      // count-up finish); gold still updates.
      homeScreen?.updatePlayerData({ gold: data.gold });
    } else {
      homeScreen?.updatePlayerData({ stacks: data.stacks, gold: data.gold });
    }
  });

  // Connect-account 100M bonus granted → notes fly to the home Stacks pill.
  networkManager.on('account:connect_bonus_result', (data) => {
    if (!data.success) return;
    localConnectBonusClaimed = true;
    void presentBalanceGain(data.newBalance);
  });

  // Server-side auth failures (e.g. a rejected social token) → surface to player.
  networkManager.on('auth:error', (data) => {
    const msg = data?.code === 'BAD_TOKEN'
      ? 'Couldn’t verify your Google sign-in. Please try again.'
      : 'Couldn’t sign you in. Please try again.';
    errorModal.show('Sign-in failed', msg);
  });

  // Notification events
  networkManager.on('notification:list', (data: NotificationListPayload) => {
    notificationsScreen?.setNotifications(data.notifications);
    homeScreen?.updatePlayerData({ notifications: data.unseenCount });
  });

  networkManager.on('notification:new', (notif: GameNotification) => {
    notificationsScreen?.addNotification(notif);
    // Increment badge — server will also send unseen_count, but this provides instant feedback
  });

  networkManager.on('notification:removed', (data: { notificationId: string }) => {
    notificationsScreen?.removeNotification(data.notificationId);
  });

  networkManager.on('notification:unseen_count', (data: { count: number }) => {
    homeScreen?.updatePlayerData({ notifications: data.count });
  });

  networkManager.on('player:gear_updated', (data: DiceGearUpdatedPayload) => {
    if (!diceScreen) return;
    // Owned-dice grant (tutorial/tasks): rebuild the grid from the full list.
    if (data.ownedDice && diceCdnBase) {
      const items: DiceItem[] = data.ownedDice.map(id => ({
        id,
        thumbnailUrl: `${diceCdnBase}/dice/${id}/thumb-128.png`,
      }));
      diceScreen.setOwnedDice(items);
      // Preload the newly-granted models so equipping them is instant.
      diceAssetManager?.preload(data.ownedDice).catch(() => { /* non-blocking */ });
    }
    if (data.presets) diceScreen.setPresets(data.presets);
    if (data.activePreset !== undefined) diceScreen.setActivePreset(data.activePreset);
  });
  networkManager.on('match:found', (payload) => {
    // Server has already removed us from lobby presence; mirror that on the
    // client so we stop receiving lobby broadcasts while in the game.
    lobbyService?.disconnect();
    // Parse the actual gameType from the roomId (server-side
    // GameRoomManager.createRoom encodes it as `<gameType>_...`).
    const prefix = (payload.roomId ?? '').split('_')[0];
    const gameType: 'ceelo' | 'craps' = prefix === 'craps' ? 'craps' : 'ceelo';
    startGame('matchmaking', gameType);
  });
  networkManager.on('match:error', (error) => {
    console.warn('[main.ts] Match error:', error);
    // Unstick the player: drop the loader and tell them what happened. No screen
    // change happened on the way in (startGame only runs on match:found), so
    // hiding the loader returns them to the lobby/home they triggered from,
    // where they can try again.
    void loadingScreen.hide();
    showToast(friendlyMatchError(error), { type: 'error' });
  });
  // The room was allocated (match:found) but the game-server socket never
  // connected. Don't strand the player in a half-built scene: drop the loader,
  // explain, and tear the session down to a usable screen.
  networkManager.on('game:connect_failed', () => {
    console.warn('[main.ts] Game server connection failed');
    void loadingScreen.hide();
    showToast("Couldn't reach the table. Try again.", { type: 'error' });
    if (inGame) {
      exitMatchToHome();
    } else {
      uiManager?.navigateTo('lobby');
    }
  });
  // If the player chose Google at the login screen, sign in socially instead of
  // as a guest (linking the device token so they re-auth seamlessly).
  if (pendingSocialAuthMain) {
    networkManager.loginSocial({
      ...pendingSocialAuthMain,
      guestToken: localStorage.getItem('sdl_auth_token') ?? undefined,
    });
  }
  networkManager.connectMain();

  // New social identity → prompt for a name, then resend with it. The prompt
  // runs a live availability check while typing (Phone Dice RequestTimer
  // pattern: debounced, stale responses dropped); the server re-validates on
  // submit and bounces with `takenName` if a race slipped through.
  const usernameChecker = new DebouncedUsernameChecker(
    (requestId, name) => networkManager?.checkUsername(requestId, name),
  );
  networkManager.on('username:check_result', (d) => {
    usernameChecker.handleResult(d.requestId, d.available, d.username);
  });
  const ensureUsernamePrompt = (): UsernamePromptModal => {
    if (usernamePrompt) return usernamePrompt;
    usernamePrompt = new UsernamePromptModal();
    const prompt = usernamePrompt;
    prompt.on('input', ({ name }) => {
      const trimmed = name.trim();
      // Blank falls back to the default name, and a linking guest keeping
      // their own current name is always allowed — no check for either.
      if (!trimmed || trimmed.toLowerCase() === prompt.getDefaultName().toLowerCase()) {
        usernameChecker.cancel();
        prompt.setAvailability('idle');
        return;
      }
      prompt.setAvailability('checking');
      usernameChecker.query(trimmed, (available) => {
        prompt.setAvailability(available ? 'available' : 'taken');
      });
    });
    return prompt;
  };
  // Console debug helper (same pattern as togglePhysicsDebug): pop the name
  // prompt for styling/UX work — live availability check included; submit is
  // inert (no pendingSocialAuthMain). showUsernamePrompt('Player1234')
  (window as any).showUsernamePrompt = (defaultName = 'Player1234') => {
    ensureUsernamePrompt().show(defaultName);
  };
  networkManager.on('auth:social_needs_username', (data) => {
    const prompt = ensureUsernamePrompt();
    prompt.show(data.defaultName);
    if (data.takenName) prompt.showTaken(data.takenName);
    prompt.once('submit', ({ name }) => {
      usernameChecker.cancel();
      const last = pendingSocialAuthMain;
      if (!last) return;
      networkManager?.loginSocial({
        ...last,
        username: name,
        guestToken: localStorage.getItem('sdl_auth_token') ?? undefined,
      });
    });
  });

  gameFlowController = new GameFlowController({
    networkManager,
  });

  const nm = networkManager;
  playerProfilesOverlay = new PlayerProfilesOverlay({
    container: uiContainer,
    serverNow: () => nm.serverNow(),
  });
  countdownOverlay = new CountdownOverlay({ container: uiContainer });

  // Wire overlays to the controller NOW, before any server broadcast can
  // arrive. Scene setup happens later and re-wires scene-dependent bits; the
  // overlays don't depend on the 3D scene, and if we wait until after scene
  // load, a STARTING broadcast that lands during GLB load would hit a null
  // overlay (showOrUpdate is optional-chained, so it silently no-ops) —
  // late-joining players would miss the countdown entirely.
  gameFlowController?.setPlayerProfilesOverlay(playerProfilesOverlay);
  gameFlowController?.setCountdownOverlay(countdownOverlay);

  // In-game menu (slide-up bottom sheet) opened from the local player's
  // profile-card hamburger badge. Subscribed here against the live overlay —
  // initializeUI() ran earlier when this overlay was still null. Chat reuses
  // the decoupled StandaloneChat; Exit runs the leave-to-home teardown.
  inGameMenu = new InGameMenu({
    container: uiContainer,
    // Borrow the real Settings audio panel so the in-game Sound view and the
    // Settings tab share one implementation + the same settingsStore.
    getSoundPanel: () => settingsScreen!.buildAudioPanel(),
  });
  inGameMenu.on('chat', () => openStandaloneChat());
  inGameMenu.on('matchchat', () => inGameChat?.openHistory());
  inGameMenu.on('exit', () => exitMatchToHome());
  playerProfilesOverlay.on('menu:open', () => inGameMenu?.open());

  // In-match public chat: green badge on the local avatar opens the compose
  // bar (free text + quick emotes); per-player bubbles ride the profile cards;
  // the menu's "Match Chat" item opens the history modal. Lives on the same
  // UI container, owns its own NetworkManager + overlay refs.
  if (networkManager) {
    inGameChat = new InGameChat({
      container: uiContainer,
      networkManager,
      profiles: playerProfilesOverlay,
      getLocalPlayerId: () => localPlayerId,
    });
  }

  // Voice chat: the WebRTC mesh transport behind the existing PTT button and
  // the per-opponent speaker/mute icon. VAD speaking state drives the same
  // indicators the cards already render.
  voiceClient = new VoiceClient(nm, {
    onSpeaking: (peerId, speaking) => playerProfilesOverlay?.setSpeaking(peerId, speaking),
    onPeerMuteChange: (peerId, muted) => playerProfilesOverlay?.setPeerMuted(peerId, muted),
  });
  // Push-to-talk: hold to talk. resumeAudio() rides the press (a user gesture)
  // so iOS lifts the AudioContext autoplay block on the first hold.
  playerProfilesOverlay.on('ptt:start', () => { voiceClient?.resumeAudio(); voiceClient?.setMuted(false); });
  playerProfilesOverlay.on('ptt:stop', () => voiceClient?.setMuted(true));
  // Tap an opponent's speaker icon to mute/unmute them locally (the
  // togglePeerMute → onPeerMuteChange callback repaints their icon).
  playerProfilesOverlay.on('peer:mute_toggle', ({ playerId }) => voiceClient?.togglePeerMute(playerId));
  // Auto-join the mesh once the server confirms we're seated (first game:update);
  // leave on disconnect. Both calls are idempotent.
  nm.on('game:update', () => maybeJoinVoice());
  nm.on('game:disconnected', () => voiceClient?.leave());
  voiceClient.setPlaybackVolume(settingsStore.get('audio').voice);

  // Setup game state event handlers
  setupGameStateHandlers();

  // Show the UI (ensure it's visible)
  uiManager.show();

  // Wait for auth before hiding loading screen (so home screen has player data)
  if (!authReady) {
    console.log('[BOOT] A. main() waiting on auth gate (authReady=false, 8s timeout)...');
    await new Promise<void>((resolve) => {
      const check = () => { if (authReady) resolve(); };
      networkManager!.on('auth:success', check);
      // Timeout fallback — don't block forever if server is down
      setTimeout(() => { console.log('[BOOT] A! auth gate TIMEOUT fired (8s, no auth)'); resolve(); }, 8000);
    });
  }
  console.log('[BOOT] B. auth gate passed (authReady=' + authReady + ') — hiding loader...');

  // Keep the loader up until the dice-model catalog has finished preloading, so
  // the dice are ready before the home screen appears. Bounded by a timeout so a
  // stuck CDN fetch can never trap the player on the loading screen.
  if (modelsPreloadPromise) {
    console.log('[BOOT] B2. waiting on dice-model preload before showing home...');
    await Promise.race([
      modelsPreloadPromise,
      new Promise<void>((resolve) => setTimeout(() => {
        console.log('[BOOT] B2! dice-model preload gate TIMEOUT (20s) — showing home anyway');
        resolve();
      }, 20000)),
    ]);
    console.log('[BOOT] B2. dice-model preload gate passed');
  }

  // Hide loading screen
  await loadingScreen.hide();
  console.log('[BOOT] C. loader hidden — boot complete');
  loaderDismissed = true;

  // Offers may have arrived during boot — surface the startup popup now the
  // home screen is visible.
  maybeShowStartupOffers();

  // Now that the loader is gone and the home screen is visible, animate the
  // first-login Welcome modal in (queued during auth:success above).
  if (pendingWelcomeGate) {
    pendingWelcomeGate = false;
    showWelcomeGate();
  }

  console.log('✅ Game initialized! Showing main menu.');
}

/**
 * Initialize the UI system
 */
function initializeUI(container: HTMLElement): UIManager {
  const ui = new UIManager({
    container,
    initialScreen: 'home',
  });

  // Create and register screens
  homeScreen = new HomeScreen();
  ui.registerScreen('home', homeScreen.element);

  // Dice screen (populated from server on auth:success)
  diceScreen = new DiceScreen();
  ui.registerScreen('dice', diceScreen.element);

  // Debounced equip: send after 500ms of no further equip events
  diceScreen.on('equip', (data) => {
    if (equipDebounceTimer) clearTimeout(equipDebounceTimer);
    equipDebounceTimer = setTimeout(() => {
      equipDebounceTimer = null;
      const slots = diceScreen?.getPresetSlots(data.preset);
      if (slots) networkManager?.sendEquipDice(data.preset, slots);
    }, 500);
  });

  // Preset switch: flush any pending equip, then send switch
  diceScreen.on('preset:switch', (data) => {
    if (equipDebounceTimer) {
      clearTimeout(equipDebounceTimer);
      equipDebounceTimer = null;
      // Flush the pending equip for the *previous* preset (already committed locally)
    }
    networkManager?.sendSwitchPreset(data.preset);

    // Preload the new preset's dice in the background
    if (diceAssetManager && diceScreen) {
      const slots = diceScreen.getPresetSlots(data.preset);
      if (slots) {
        const ids = slots.filter(Boolean) as string[];
        if (ids.length > 0) {
          diceAssetManager.preload(ids).catch(() => {});
        }
      }
    }
  });

  crewScreen = new CrewScreen();
  ui.registerScreen('crew', crewScreen.element);

  // Lobby screen (matchmaking + public chat). Service is connected on
  // lobby entry / disconnected on exit via the screen:change handler below.
  lobbyService = new LobbyService();
  lobbyScreen = new LobbyScreen(lobbyService, {
    id: 'local',
    username: 'YOU',
    ringColor: 'gold',
  });
  ui.registerScreen('lobby', lobbyScreen.element);
  lobbyScreen.on('back:tap', () => ui.navigateTo('home'));
  // Body-level player card shared by lobby + in-game profile taps. The
  // controller itself is constructed in main() once networkManager exists
  // (initializeUI runs before that). These handlers read the module-level
  // `playerCardController` at tap time, by which point it's set.
  lobbyScreen.on('profile:tap', ({ playerId, username, picture }) => {
    if (!playerCardController || playerId === localPlayerId) return;
    // Presence data wins; fall back to caller-provided info (e.g. a chat sender
    // who isn't in the lobby presence list because they're already in a game).
    const p = lobbyService?.getPlayer(playerId);
    playerCardController.show({
      id: playerId,
      username: p?.username ?? username ?? 'Player',
      picture: p?.picture ?? picture ?? '',
      isOnline: p?.status === 'online',
    });
  });

  // In-game: tap an opponent's profile card to open the same player card.
  playerProfilesOverlay?.on('profile:tap', ({ playerId, username, picture, crewTag }) => {
    if (!playerCardController || playerId === localPlayerId) return;
    playerCardController.show({ id: playerId, username, picture, crewTag });
  });

  // Create-room modal: opens from CreateRoomPill, submits via LobbyService,
  // game handoff is the existing match:found → startGame path.
  createRoomModal = new CreateRoomModal();
  document.body.appendChild(createRoomModal.element);
  createRoomModal.on('submit', (payload) => {
    createRoomModal?.hide();
    loadingScreen.show();
    lobbyService?.submitCreateRoom(payload);
  });
  lobbyScreen.on('create_room:tap', () => {
    createRoomModal?.show({ balanceStacks: localBalance.stacks });
  });
  lobbyScreen.on('join_room:tap', () => {
    // Server reply (match:found) drives the actual transition; show loading
    // immediately so the user gets feedback while we wait for the ack.
    loadingScreen.show();
  });

  // Notifications screen (modal-only, not registered as a navigable screen)
  notificationsScreen = new NotificationsScreen();

  shopScreen = new StoreScreen();
  ui.registerScreen('shop', shopScreen.element);
  refreshShopWallet();

  // In-App Purchases: native via cordova-plugin-purchase, dev stub in the browser.
  // The SERVER delivers the goods (validates + credits); we order, forward the
  // receipt, finish(), and render the grant.
  iapManager = new IapManager({
    getToken: () => authToken,
    getBalance: () => ({ stacks: localBalance.stacks, gold: localBalance.gold, diceTokens: localBalance.diceTokens }),
    devGrant: (productId) => shopScreen?.getBundleAmounts(productId) ?? null,
    onError: (productId, message) => {
      console.warn('[iap] purchase failed:', productId, message);
      if (message !== 'cancelled') showToast('Purchase could not be completed. Try again');
    },
    onGranted: (granted, balance, ctx) => { void handleIapGranted(granted, balance, ctx.productId, ctx.real); },
  });
  void iapManager.init();

  shopScreen.on('purchase', (raw: unknown) => {
    const { productId } = raw as { productId: string };
    void iapManager?.purchase(productId, { surface: 'store_browse' });
  });
  shopScreen.on('claim-chest', () => {
    console.log('[store] surprise chest claim requested');
    // TODO: server roll + reward animation
  });

  // Offer popup: a baked card the player taps. Paid → IAP (same grant path as
  // the store); free/video → server claim. Tapping an OFFERS-tab card opens it.
  offerPopup = new OfferPopup({
    onPaid: (offer) => {
      if (offer.storeid) void iapManager?.purchase(offer.storeid, { surface: 'startup_popup', offerId: offer.id });
    },
    onClaim: (offer, adsWatched) =>
      networkManager?.claimOffer(offer.id, adsWatched != null ? { adsWatched } : undefined),
    adProvider: createAdProvider(),
  });
  shopScreen.on('offer:tap', (raw: unknown) => {
    const { offer } = raw as { offer: OfferWire };
    offerPopup?.show([offer]);
  });

  settingsScreen = new SettingsScreen();
  ui.registerScreen('settings', settingsScreen.element);

  // Change profile picture: pick → crop → upload to Bunny CDN → refresh the
  // account card. Mirrors the UDG Poker flow (@capacitor/camera + croppie).
  avatarUploadController = new AvatarUploadController({
    container,
    getToken: () => authToken,
    onUploaded: (picture) => {
      settingsScreen?.setAccount({ picture });
      homeScreen?.updatePlayerData({ avatar: picture });
    },
  });
  settingsScreen.on('account:change-picture', () => {
    avatarUploadController?.change();
  });
  settingsScreen.on('account:update-email', () => {
    console.log('[settings] update-email requested — TODO: server endpoint');
  });
  settingsScreen.on('account:recover', () => {
    console.log('[settings] account recover/register requested — TODO: server flow');
  });
  // Guest tapped a provider in the "Connect & bag 100M" section. Run the social
  // sign-in, then link it onto the CURRENT guest (guestToken) so dice/progress
  // carry over. The server re-emits AUTH_SUCCESS (guarded above) and we claim
  // the 100M via triggerConnectBonus on that re-entry. New identities still go
  // through the existing auth:social_needs_username name prompt.
  settingsScreen.on('account:connect', async (raw) => {
    const { provider } = raw as { provider: 'google' | 'apple' };
    let result;
    try {
      result = provider === 'google' ? await googleSignIn() : await appleSignIn();
    } catch (e) {
      console.error(`[settings:connect] ${provider} sign-in error`, e);
      const name = provider === 'google' ? 'Google' : 'Apple';
      errorModal.show('Sign-in failed', `Couldn’t sign in with ${name}. Please try again.`);
      return;
    }
    if (!result) return; // user cancelled — stay put
    pendingSocialAuthMain = { provider, token: result.token, tokenType: result.tokenType };
    networkManager?.loginSocial({
      ...pendingSocialAuthMain,
      guestToken: localStorage.getItem('sdl_auth_token') ?? undefined,
    });
  });
  settingsScreen.on('account:logout', async () => {
    // Confirm first — logout wipes local state and forces a re-login.
    const ok = await confirmModal.show({
      title: 'Log out?',
      message: "You'll need to sign back in to keep playing.",
      confirmLabel: 'LOG OUT',
      cancelLabel: 'STAY',
      danger: true,
    });
    if (!ok) return;
    // Sign-out must ALWAYS reach the reload below. Every cleanup step is
    // best-effort and time-boxed so a hung native call (FCM deleteToken, the
    // Google SDK) can never strand the user on a logged-in screen.
    const capped = (p: Promise<unknown>, ms: number): Promise<unknown> =>
      Promise.race([Promise.resolve(p).catch(() => undefined), new Promise((r) => setTimeout(r, ms))]);
    // Stop this device receiving the previous account's pushes (the server-side
    // unregister emit inside clearOnLogout is synchronous; only token deletion
    // can be slow, so we cap it).
    await capped(notificationService.clearOnLogout(), 1500);
    // Wipe ALL persisted app state, not just the auth keys — settings,
    // tutorial-seen flags, chat bubble position etc. must not leak into the
    // next account. Prefix-scoped (sdl_/sdl-) so WebView/plugin storage
    // (e.g. Google sign-in SDK state) survives.
    try {
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('sdl_') || key.startsWith('sdl-')) {
          localStorage.removeItem(key);
        }
      }
    } catch { /* private-mode / quota — nothing persisted anyway */ }
    // Clear the native Google session BEFORE the reload kills the bridge —
    // a stale session makes the next GoogleAuth.signIn reject with
    // "Something went wrong" right after account selection. Capped: a guest
    // (no Google session) or a slow native bridge must not block the reload.
    await capped(googleSignOut(), 2000);
    window.location.reload();
  });
  settingsScreen.on('account:unblock', ({ playerId }) => {
    networkManager?.sendFriendUnblock(playerId);
    blockService.remove(playerId); // optimistic — server re-sends blocked:list
  });

  // Push notifications toggle. ON is an explicit ask, so prompting the OS here
  // is fine; if the player previously denied at the OS level, nudge them to
  // Settings (we can't re-open the system prompt). OFF mutes server-side.
  settingsScreen.on('notifications:toggle', async ({ enabled }) => {
    if (enabled) {
      const result = await notificationService.enable();
      if (result === 'denied') {
        showToast('Turn on notifications for Street Dice Legends in your phone Settings');
      }
    } else {
      await notificationService.disable();
    }
    // Re-read the real permission so the toggle reflects the actual result
    // (e.g. snaps back OFF if the OS prompt was denied).
    void settingsScreen?.refreshNotificationState();
  });

  // Support — needs the admin-server SupportTicket endpoint wired through
  // main-server. For now we log; swap to a socket emit once the route exists.
  settingsScreen.on('support:open-ticket', (raw) => {
    const { subject, message } = raw as { subject: string; message: string };
    console.log('[settings] support ticket:', { subject, message });
  });
  settingsScreen.on('support:feedback', (raw) => {
    const { message } = raw as { message: string };
    console.log('[settings] feedback:', message);
  });
  // Manual "Rate us" → store listing (or native sheet fallback on iOS pre-id).
  settingsScreen.on('support:rate', () => {
    void ratingService.openStoreListing();
  });

  // Handle home screen actions
  homeScreen.on('action', (data) => {
    const { action } = data as { action: string };
    handleHomeAction(action);
  });

  // Refresh home data every time the home screen becomes visible
  ui.on('screen:change', (raw: unknown) => {
    const data = raw as { from: string; to: string };
    if (data.to === 'home' && networkManager && authReady) {
      networkManager.requestHomeData();
    }
    if (data.to === 'crew') {
      crewScreen?.onShow();
    }
    if (data.to === 'settings') {
      settingsScreen?.onShow();
    }
    // Lobby socket lifecycle: subscribe on entry, unsubscribe on any exit.
    // Server-side presence is cleared on disconnect as well, so this only
    // matters while the user is actively connected.
    if (data.to === 'lobby' && networkManager && authReady) {
      lobbyService?.connect(networkManager);
    } else if (data.from === 'lobby') {
      lobbyService?.disconnect();
    }
    // Reset the store scroll when leaving — next visit lands on the chest hero.
    if (data.from === 'shop') {
      shopScreen?.resetScroll();
    }
  });

  // Refresh home data / in-game state when app returns from background.
  // Mobile Capacitor suspends JS while backgrounded; on return we can't
  // trust in-memory state. We also flip the server-side isActive flag so
  // the server doesn't stall waiting on a backgrounded client's lock-in.
  document.addEventListener('visibilitychange', () => {
    if (!networkManager || !authReady) return;

    if (document.visibilityState === 'hidden') {
      if (inGame) {
        // Best-effort: tell the server we're gone before the OS kills the
        // runtime. On mobile this flush may not make it through, in which
        // case the server's existing disconnect timeout is the fallback.
        networkManager.sendActiveStatus(false);
      }
      return;
    }

    // Visible / foregrounded:
    // Always notify server of resume — server bumps lastLogin + records a
    // daily-login event if the session has been idle >8h, so a desktop
    // browser tab whose socket never dropped still counts toward DAU.
    networkManager.sendClientResume();
    // Re-pull offers on foreground — eligibility (e.g. first-open-of-day,
    // schedule windows) may have changed while backgrounded.
    offerService?.refresh();
    if (inGame) {
      // refreshGameStateFromServer reconnects the game channel first, then
      // flips active=true and requests state — so we never emit into a dead
      // socket (which would hang the resume). state:full is processed through
      // the state machine, so a backgrounded mid-drag roller has their dice
      // re-spawned automatically: the resumed roll state's onEnter sees the
      // local id in update.rollAuthorizedFor and calls setupLocalRoll. Roll
      // authorization is resume-safe STATE now — no manual re-authorize.
      refreshGameStateFromServer().catch((err) => {
        console.warn('[main] resume refresh failed:', err);
      });
    } else {
      networkManager.requestHomeData();
    }
  });

  return ui;
}

/**
 * Resume flow: show loading, reset the state machine (so the incoming update
 * triggers a fresh onEnter), request a full state from the server, wait for
 * the state machine to finish processing it, then dismiss loading. No
 * animations — the goal is to snap the UI to current reality, not replay.
 */
async function refreshGameStateFromServer(): Promise<void> {
  if (resumeRefreshInFlight) return;
  if (!networkManager || !gameFlowController) return;
  resumeRefreshInFlight = true;

  try {
    loadingScreen.show();
    // Reconnect the game channel before emitting anything — after a long
    // background the socket is dead, and state:request into a dead socket
    // just hangs. ensureGameConnected forces the reconnect (and re-join).
    await networkManager.ensureGameConnected();
    // Flip active=true now that we're connected, so the server's state reply
    // reflects us being back in the round.
    networkManager.sendActiveStatus(true);
    gameFlowController.resetStateMachine();
    networkManager.sendStateRequest();

    // Wait for the state machine to process the incoming state:full — same
    // barrier used on initial game entry. Fail-safe timeout if the server
    // never answers (socket dead, etc.).
    await Promise.race([
      gameFlowController.getFirstUpdateProcessed(),
      new Promise<void>((resolve) => setTimeout(resolve, 8000)),
    ]);
  } finally {
    await loadingScreen.hide();
    resumeRefreshInFlight = false;
  }
}

/**
 * Setup game state event handlers
 */
function setupGameStateHandlers(): void {
  if (!gameStateManager) return;

  // When game starts - setup scene, then signal startGame() when done
  gameStateManager.on('game:started', async (data) => {
    const { config } = data as { config: { mode: string; gameType?: 'ceelo' | 'craps' } };
    console.log('Game started:', config);
    inGame = true;

    // Snapshot balance for net-win detection + bump the rating engagement count.
    preGameStacks = localBalance.stacks;
    try {
      const n = Number(localStorage.getItem(RATE_GAMES_KEY) || '0') + 1;
      localStorage.setItem(RATE_GAMES_KEY, String(n));
    } catch { /* private mode / quota — rating gate just stays conservative */ }

    // Pick the scene's GameModeId. Solo practice uses its own scene id;
    // matchmaking/friends use the actual gameType chosen at match time.
    const gameModeId: import('@sdl/scene-format').GameModeId =
      config.mode === 'solo'
        ? 'solo'
        : config.gameType === 'craps' ? 'craps' : 'ceelo';

    // Load currency note definitions from server (for bill textures/denominations)
    const { fetchCurrencyNotes } = await import('./api/currencyNoteApi');
    await fetchCurrencyNotes().catch(() => {}); // Non-blocking, fallback to hardcoded

    await setupGameScene(gameModeId);

    // Scene + controllers ready → NOW connect to game server (UDG pattern).
    // state:full will be processed immediately by the state machine since
    // all controllers exist.
    // Reset the state machine so each game session gets a fresh
    // firstUpdateProcessed barrier — otherwise the promise from a previous
    // game is already resolved and the loading screen doesn't actually wait
    // for the new game's initial onEnter.
    gameFlowController?.resetStateMachine();
    networkManager?.connectToGameServer();

    // Wait for the state machine to FINISH processing the initial update
    // (state:full → onEnter → countdown / profiles rendered), not just the
    // event arrival. The TaskPool is async so event listeners fire before
    // processUpdate completes — if we resolve on event arrival the loading
    // screen can dismiss to an empty scene and players miss the countdown.
    await Promise.race([
      gameFlowController?.getFirstUpdateProcessed() ?? Promise.resolve(),
      new Promise<void>((resolve) => setTimeout(resolve, 8000)), // fail-safe
    ]);

    // Signal that scene is fully loaded and state applied
    sceneSetupResolve?.();
  });

  // When game ends
  gameStateManager.on('game:ended', () => {
    console.log('Game ended, returning to menu');
    returnToHomeFromGame();
  });

}

/**
 * Tear down the live game scene and return to the home screen. Shared by the
 * server-driven `game:ended` path and the menu's "Exit Match" button.
 */
function returnToHomeFromGame(): void {
  inGame = false;

  // Hide game container (so 3D scene doesn't show through UI)
  const gameContainer = document.getElementById('game-container');
  if (gameContainer) {
    gameContainer.style.visibility = 'hidden';
  }

  // Show main UI
  uiManager?.show();
  uiManager?.navigateTo('home');

  // Clear game scene elements
  cleanupGameScene();

  // Recap net winnings on the home pill (navigateTo above no-ops when already
  // on 'home', so screen:change won't fire — trigger the check directly). Then,
  // once the money animation has landed, ride that winning beat into the rating
  // ask (self-gates on net-win + eligibility, mirroring the notification primer).
  setTimeout(() => {
    void (async () => {
      await checkHomeBalanceGain();
      await maybeAskForRating();
    })();
  }, 600);
}

/**
 * Menu "Exit Match" → deliberate leave. Tells the server to clean-remove +
 * settle our seat (NOT the keep-seat/reconnect path), drops the saved
 * reconnect data so we don't auto-rejoin on next launch, then runs the same
 * teardown-to-home as a natural game end. We don't wait for a server echo —
 * the player asked to leave, so we leave immediately.
 */
function exitMatchToHome(): void {
  voiceClient?.leave();
  networkManager?.leaveRoom();
  try {
    localStorage.removeItem('sdl_reconnect_token');
    localStorage.removeItem('sdl_room_id');
  } catch { /* private-mode / quota — non-fatal */ }
  // End via the state manager so engine.stop() runs — otherwise the GameLoop
  // keeps stepping physics and re-rendering the emptied scene into the hidden
  // game-container every frame, stealing the shared canvas from any UI view
  // (e.g. the bonus suitcase) and burning battery on the home screen.
  // endGame() emits 'game:ended' → returnToHomeFromGame().
  if (gameStateManager && gameStateManager.getState() !== 'menu') {
    gameStateManager.endGame();
  } else {
    returnToHomeFromGame();
  }
}

/**
 * Setup the game scene with enhanced visuals
 */
async function setupGameScene(gameModeId?: import('@sdl/scene-format').GameModeId): Promise<void> {
  if (!engine) return;

  // Create and initialize game scene (loads from server or falls back to hardcoded)
  gameScene = new GameScene(engine, {
    tableWidth: 12,
    tableDepth: 16,
    wallHeight: 8,
    gameModeId,
  });
  await gameScene.initialize();

  // The served scene document authors its lights at shadowMapSize 2048. Re-apply
  // the current quality tier now that the lights exist so the shadow map matches
  // the tier (high = 1024, à la Phone Dice) instead of the document's 2048.
  // This is also the only path that applies the tier's shadow size to scene
  // lights — SharedRenderer.setQuality only touches pixel ratio.
  engine.setQuality(settingsStore.get('graphics').quality);

  // Initialize dice controller (settings match prototype exactly)
  diceController = new DiceController(engine, {
    diceCount: 3,
    energyGain: 0.3,      // Energy gain per pixel of movement
    maxEnergy: 100,
    minEnergyToThrow: 5,
    maxPower: 35,         // Throw power (prototype: 35)
    baseForward: 5,       // Base forward force (prototype: 5)
    holdHeight: 4,        // Hold height (prototype: 4)
    pickupRadius: 1.5,    // Pickup radius
  });

  // Wire up dice asset manager for custom dice skins
  if (diceAssetManager) {
    diceController.setAssetManager(diceAssetManager);

    // Get equipped dice from the active preset
    const activePreset = diceScreen?.getActivePreset() ?? 0;
    const equippedSlots = diceScreen?.getPresetSlots(activePreset);
    if (equippedSlots) {
      const equippedIds = equippedSlots.filter(Boolean) as string[];
      diceController.setEquippedDice(equippedIds);

      // Ensure assets are loaded before creating dice (with 5s timeout)
      if (equippedIds.length > 0) {
        await Promise.race([
          diceAssetManager.preload(equippedIds),
          new Promise((resolve) => setTimeout(resolve, 5000)),
        ]);
      }
    }
  }

  // Dice creation is now owned by the state machine (WaitingStateHandler.onEnter
  // creates practice dice, HI_LO/roll handlers create game dice). No manual
  // creation here — it would bypass the state machine and leave stale dice
  // when transitioning to STARTING or HI_LO.

  // Initialize camera controller
  cameraController = new CameraController(engine.camera, {
    defaultPosition: { x: 0, y: 14, z: 10 },
    defaultLookAt: { x: 0, y: 0, z: 0 },
    lerpSpeed: 3,
    zoomLerpSpeed: 2,
    minHeight: 6,
    padding: 2.5,
    diceSize: 1,
  });

  gameFlowController?.setEngine(engine);
  gameFlowController?.setDiceAssetManager(diceAssetManager);
  gameFlowController?.setCdnBase(diceCdnBase);
  gameFlowController?.setDiceController(diceController);
  gameFlowController?.setCameraController(cameraController);
  gameFlowController?.setCameraAnchors(gameScene.getCameraAnchors());
  gameFlowController?.setAnchoredLights(gameScene.getAnchoredLights());
  gameFlowController?.setPlayerProfilesOverlay(playerProfilesOverlay);
  gameFlowController?.setCountdownOverlay(countdownOverlay);

  if (!gameNetworkHandlersBound && networkManager) {
    gameNetworkHandlersBound = true;
    // Camera dice ownership lives entirely in GameFlowController / state machine
    // (driven by game:update). No game:state listener here — it clobbered the
    // spectator's opponent dice after handleNewRollResult set zoom mode.
  }

  // Initialize betting system
  const qualitySettings = engine.renderer.getBettingQualitySettings();
  bettingAssetLoader = new AssetLoader({
    quality: qualitySettings.walletModel as 'low' | 'medium' | 'high',
    basePath: '/assets',
  });
  bettingController = new BettingController(engine, bettingAssetLoader, {
    initialBalance: 10000,
    minBet: 100,
    maxBet: 1000000,
    swipeThreshold: 30,
    uiContainer: document.getElementById('ui-container') ?? undefined,
    // Apply the render quality tier to bills (segments / shadows / texture size)
    // so Low/Medium actually lighten the betting scene.
    billSegments: qualitySettings.billSegments,
    billShadows: qualitySettings.billShadows,
    billTextureSize: qualitySettings.billTextureSize,
  });

  try {
    await bettingController.initialize();
    await prewarmObjectVisibility(engine, bettingController.getWalletGroup(), 1, {
      hideOffscreen: true,
    });
  } catch (error) {
    console.error('Failed to initialize betting controller', error);
  }

  bettingHUD3D = new BettingHUD3D({
    scene: engine.scene,
    camera: engine.camera,
    assetLoader: bettingAssetLoader ?? undefined,
  });
  bettingHUD3D.attachInput(engine.canvas);

  // Create DOM sidebet buttons overlay (positioned relative to wallet)
  const uiCont = document.getElementById('ui-container');
  if (uiCont) {
    sidebetButtonsOverlay = new SidebetButtonsOverlay({
      container: uiCont,
      camera: engine.camera,
      canvas: engine.canvas,
    });
    cancelBetOverlay = new CancelBetOverlay({
      container: uiCont,
      camera: engine.camera,
      canvas: engine.canvas,
    });
    instructionBanner = new InstructionBanner({ container: uiCont });
  }

  gameFlowController?.setCancelBetOverlay(cancelBetOverlay);
  gameFlowController?.setInstructionBanner(instructionBanner);
  gameFlowController?.setBettingController(bettingController);
  gameFlowController?.setBettingHUD3D(bettingHUD3D);
  gameFlowController?.setSidebetButtonsOverlay(sidebetButtonsOverlay);

  // Wallet tutorial label is owned by the Wallet itself (show on open-complete,
  // hide on close-start). We only feed it the two gesture flags here.
  bettingController.on('betting:bill_placed', () => {
    bettingController?.getWallet().getInstructionsLabel()?.markTapUsed();
  });
  bettingController.on('betting:denomination_changed', () => {
    bettingController?.getWallet().getInstructionsLabel()?.markHoldUsed();
  });

  bettingController.on('betting:banker_pot_requested', (data) => {
    networkManager?.sendBet(data.amount, data.denominations);
    // Hold the local balance preview across the commit→server-deduct round trip.
    const lid = networkManager?.getPlayerId();
    if (lid) playerProfilesOverlay?.commitLocalPendingBet(lid, data.amount);
  });

  bettingController.on('betting:zone_fade_requested', (data) => {
    networkManager?.sendBetZoneFade(data.requestId, data.zoneId, data.amount, data.denominations);
    const lid = networkManager?.getPlayerId();
    if (lid) playerProfilesOverlay?.commitLocalPendingBet(lid, data.amount);
  });

  bettingController.on('betting:zone_create_requested', (data) => {
    const betTypeMap: Record<string, string> = {
      // Cee-lo
      '4+': 'four_up',
      '3-': 'three_down',
      'BANK WINS': 'bank_wins',
      'BANK LOSES': 'bank_loses',
      // Craps — pre-come-out turn-level
      'SW': 'shooter_wins',
      'SL': 'shooter_loses',
      // Craps — mid-point multi-roll point pairs
      '4/10': 'point_pair_4_10',
      '5/9': 'point_pair_5_9',
      '6/8': 'point_pair_6_8',
    };
    const betType = betTypeMap[data.betType] ?? data.betType.toLowerCase();
    networkManager?.sendBetZoneCreate(data.requestId, data.amount, betType, gameFlowController?.getBankerId() ?? undefined, data.denominations);
  });

  // Register update callback
  engine.onUpdate((dt) => {
    diceController?.update(dt);
    bettingController?.update(dt);
    bettingHUD3D?.update(dt);
    sidebetButtonsOverlay?.update();
    cancelBetOverlay?.update();
    cameraController?.update(dt);
    gameScene?.update(dt);
    gameFlowController?.update(dt);

    // Physics debug visualization
    if (physicsDebugEnabled && engine) {
      updatePhysicsDebug(engine.physics, engine.scene);
    }
  });

  // When dice stop, zoom in and prompt player to pick them up
  diceController.on('dice:stopped', (data) => {
    const { values } = data as { values: number[] };
    const total = values.reduce((a: number, b: number) => a + b, 0);
    console.log('Dice stopped! Values:', values, 'Total:', total);
    // Cee-lo lag-trace marker (no-op for craps).
    gameFlowController?.markDiceStopped();
    // Zoom in on stopped dice
    cameraController?.setMode('zoom');
  });

  // State change handlers for dice controller events
  diceController.on('dice:pickup', () => {
    const heldCount = diceController?.collected ?? 0;
    const totalDice = diceController?.getDice().length ?? 0;
    if (heldCount < totalDice) {
      // Still collecting - zoom on remaining unheld dice
      cameraController?.setMode('zoom_unheld');
    } else {
    }
  });

  diceController.on('dice:all_collected', () => {
    // All dice collected - return to default camera (shoot position)
    cameraController?.setMode('default');
  });

  diceController.on('dice:rolling', () => {
    // Follow dice while rolling
    cameraController?.setMode('follow');
  });

  diceController.on('dice:throw', () => {
    // Start following dice when thrown
    cameraController?.setMode('follow');
  });
}

/**
 * Cleanup game scene when exiting
 */
function cleanupGameScene(): void {
  if (diceController) {
    diceController.dispose();
    diceController = null;
  }

  if (bettingController) {
    bettingController.dispose();
    bettingController = null;
  }

  if (bettingHUD3D) {
    bettingHUD3D.dispose();
    bettingHUD3D = null;
  }

  // Reset, don't destroy: the overlay is created once in main() and the home
  // screen's cash flyouts (daily bonus, connect bonus) reuse it after the
  // game. Destroying it here left presentBalanceGain's overlay null → the
  // notes-to-pill flyout silently degraded to a bare count-up, and the next
  // game got setPlayerProfilesOverlay(null).
  playerProfilesOverlay?.resetForGameExit();

  if (sidebetButtonsOverlay) {
    sidebetButtonsOverlay.destroy();
    sidebetButtonsOverlay = null;
  }

  if (cancelBetOverlay) {
    cancelBetOverlay.destroy();
    cancelBetOverlay = null;
  }

  if (instructionBanner) {
    instructionBanner.destroy();
    instructionBanner = null;
  }

  if (inGameMenu) {
    inGameMenu.destroy();
    inGameMenu = null;
  }

  if (cameraController) {
    cameraController.reset();
    cameraController = null;
  }

  gameFlowController?.setDiceController(null);
  gameFlowController?.setCameraController(null);
  gameFlowController?.setBettingController(null);
  gameFlowController?.setBettingHUD3D(null);
  gameFlowController?.setPlayerProfilesOverlay(null);
  gameFlowController?.setSidebetButtonsOverlay(null);
  gameFlowController?.setCancelBetOverlay(null);
  gameFlowController?.setInstructionBanner(null);
  gameFlowController?.setCountdownOverlay(null);

  gameScene = null;

  // Clear scene objects (but keep renderer)
  if (engine) {
    const scene = engine.scene;
    while (scene.children.length > 0) {
      scene.remove(scene.children[0]);
    }
    // Clear physics bodies
    engine.physics.dispose();
  }
}

/**
 * Handle actions from the home screen
 */
function handleHomeAction(action: string): void {

  switch (action) {
    case 'play':
      // HIT THE BLOCK opens the lobby. Matchmaking will be triggered from
      // inside the lobby (Quick Match / Join Room) once those are wired.
      uiManager?.navigateTo('lobby');
      break;
    case 'solo':
    case 'friends':
    case 'leaderboard':
    case 'crew-wars':
      // These modes are locked for the multiplayer beta. Explain the lock and
      // funnel the player toward the live tables (HIT THE BLOCK).
      showComingSoonModal(action);
      break;
    case 'daily-bonus':
      if (pendingBonusPrize != null) {
        openDailyBonus();
      } else {
        homeScreen?.showCooldownTooltip();
      }
      break;
    case 'notifications':
      if (notificationsScreen) {
        uiManager?.openModal('notifications', notificationsScreen.element);
        notificationsScreen.onOpen();
      }
      break;
    case 'stacks':
    case 'gold':
      // Navigate to shop
      uiManager?.navigateTo('shop');
      break;
    case 'profile':
      // Tap the home avatar → Settings → Account (to change the photo).
      settingsScreen?.openTab('account');
      uiManager?.navigateTo('settings');
      break;
  }
}

/**
 * Per-mode copy for the "locked during the multiplayer beta" modal. Accent +
 * icon mirror the home card so the modal reads as the same mode.
 */
const COMING_SOON_MODES: Record<string, Omit<ComingSoonOptions, 'onPlayLive'>> = {
  solo: {
    title: 'SOLO GRIND',
    iconSrc: '/assets/icons/modes/solo.svg',
    accent: '#00F5FF',
    message: "Practice your throw and stack bread on your own time. Solo Grind opens up after the multiplayer beta.",
  },
  friends: {
    title: 'RUN IT UP',
    iconSrc: '/assets/icons/modes/friends.svg',
    accent: '#FF0080',
    message: "Spin up a private table and bring your crew along. Run It Up opens up after the multiplayer beta.",
  },
  leaderboard: {
    title: 'LEADERBOARD',
    iconSrc: '/assets/icons/modes/leaderboard.svg',
    accent: '#FFD700',
    message: "See who's really running the streets. Rankings go live after the multiplayer beta.",
  },
  'crew-wars': {
    title: 'CREW WARS',
    iconSrc: '/assets/icons/modes/crew-wars.svg',
    accent: '#A855F7',
    message: "Rep your set against rival crews for turf and bragging rights. Crew Wars drops after the multiplayer beta.",
  },
};

let comingSoonOverlay: ComingSoonOverlay | null = null;

/**
 * Explain why a home mode is locked for the beta and funnel the player toward
 * the live tables (HIT THE BLOCK opens the lobby).
 */
function showComingSoonModal(mode: string): void {
  const def = COMING_SOON_MODES[mode];
  if (!def) return;
  if (!comingSoonOverlay) comingSoonOverlay = new ComingSoonOverlay();
  void comingSoonOverlay.show({
    ...def,
    onPlayLive: () => uiManager?.navigateTo('lobby'),
  });
}

/**
 * Compute suitcase cash stack count from prize amount (log-scaled 4-24).
 */
function getCashStackCount(prize: number): number {
  const MIN = 1_200_000;
  const MAX = 1_200_000_000;
  const logMin = Math.log(MIN);
  const logMax = Math.log(MAX);
  const logP = Math.log(Math.max(MIN, Math.min(MAX, prize)));
  const t = (logP - logMin) / (logMax - logMin);
  return Math.round(4 + t * 20);
}

/**
 * Open the daily bonus suitcase experience
 */
function openDailyBonus(): void {
  if (bonusSuitcaseOverlay) return; // Already open
  dismissDailyBonusNudge(); // they found it — clear the post-tutorial coach mark
  if (pendingBonusPrize == null) return; // No bonus available from server

  const uiContainer = document.getElementById('ui-container');
  if (!uiContainer) return;

  const prize = pendingBonusPrize;
  const stackCount = getCashStackCount(prize);
  const streakMult = pendingBonusMultiplier;

  let committed = false;

  bonusSuitcaseOverlay = new BonusSuitcaseOverlay(uiContainer);
  bonusSuitcaseOverlay.open(prize, stackCount, streakMult);

  // Consume the bonus ONLY when the case actually opens — anti-abuse (no
  // close-and-retry to hunt a bigger bonus; the amount is final once the case
  // opens), and merely peeking then leaving keeps it claimable. We also set
  // bonusInFlight so the bonus's BALANCE_UPDATE doesn't fire an immediate
  // background home flyout — we play that explicitly on close instead.
  bonusSuitcaseOverlay.on('opened', () => {
    if (committed) return;
    committed = true;
    bonusInFlight = true;
    networkManager?.claimDailyBonus();
    pendingBonusPrize = null;
    homeScreen?.updatePlayerData({ hasDailyBonus: false });
  });

  bonusSuitcaseOverlay.on('close', async () => {
    bonusSuitcaseOverlay?.destroy();
    bonusSuitcaseOverlay = null;
    bonusInFlight = false; // release suppression; presentBalanceGain re-manages it
    // Notes fly from the (centre-screen) suitcase to the Stacks pill + count up.
    if (committed && pendingDailyAward) {
      const award = pendingDailyAward;
      pendingDailyAward = null;
      await presentBalanceGain(award.newBalance, { x: window.innerWidth / 2, y: window.innerHeight / 2 });
      // Money has landed on the home pill — NOW offer notifications, so the
      // primer reads as a calm follow-up on the main screen rather than fighting
      // the flyout. One-shot, gated (skips if already asked / not in prompt).
      void maybePrimeNotifications();
    }
  });
}

/** Cleanup for the post-tutorial daily-bonus nudge (tap + timeout listeners). */
let dailyBonusNudgeCleanup: (() => void) | null = null;

/**
 * One-time, post-tutorial nudge: spotlight the daily-bonus gift button so new
 * players learn where their free daily reward lives and start the streak on day
 * one. The player taps the gift themselves (we don't auto-open) so the location
 * sticks for tomorrow. Fires once ever, guarded by localStorage, and only when a
 * bonus is actually available on the home screen. The coach mark is
 * pointer-events:none, so the tap that dismisses it also reaches the gift.
 */
function showDailyBonusNudge(): void {
  if (dailyBonusCoach) return;
  if (localStorage.getItem('sdl_daily_nudge_seen')) return;
  if (uiManager?.getCurrentScreen() !== 'home') return;
  if (!homeScreen?.hasDailyBonusAvailable()) return;

  localStorage.setItem('sdl_daily_nudge_seen', '1');

  const coach = new CoachMarkOverlay({ showSkip: false, highlight: true });
  dailyBonusCoach = coach;
  coach.setStep({
    text: 'Your daily bonus is waiting!',
    subtext: 'Tap the gift to claim free Stacks... come back daily to grow your streak.',
    target: () => {
      const r = homeScreen?.getDailyBonusButtonRect();
      return r ? { x: r.left, y: r.top, width: r.width, height: r.height } : null;
    },
    bannerPosition: 'bottom',
    // Gift button hugs the top of the screen, so point up at it from below.
    arrowPosition: 'bottom',
    pad: 8,
  });
  coach.show();

  // Dismiss on the first tap anywhere (the gift tap opens the suitcase AND
  // clears this via openDailyBonus), or after a timeout if the player ignores it.
  const onTap = () => dismissDailyBonusNudge();
  window.addEventListener('pointerdown', onTap, true);
  const timer = window.setTimeout(() => dismissDailyBonusNudge(), 12000);
  dailyBonusNudgeCleanup = () => {
    window.removeEventListener('pointerdown', onTap, true);
    window.clearTimeout(timer);
  };
}

/** Tear down the post-tutorial daily-bonus nudge. Idempotent. */
function dismissDailyBonusNudge(): void {
  dailyBonusNudgeCleanup?.();
  dailyBonusNudgeCleanup = null;
  if (!dailyBonusCoach) return;
  dailyBonusCoach.destroy();
  dailyBonusCoach = null;
}

/**
 * First-login tutorial gate. Lazily builds the Welcome modal and wires its two
 * paths. Skip grants the full reward immediately (balance + gear refresh arrive
 * via the existing balance:update / player:gear_updated listeners). "LET'S ROLL"
 * launches the guided tutorial — wired in a later phase (D/E).
 */
function showWelcomeGate(): void {
  if (!welcomeModal) {
    welcomeModal = new WelcomeModal();
    welcomeModal.on('skip', () => {
      networkManager?.skipTutorial();
      triggerConnectBonus();
    });
    welcomeModal.on('start', () => {
      void startTutorial();
    });
  }
  welcomeModal.show();
}

/**
 * After the tutorial ends (and any time on home), surface the 100M connect
 * bonus. Linked + unclaimed → claim it (server-authoritative; the
 * 'account:connect_bonus_result' listener plays the cash beat). Guests → the
 * persistent "connect to claim 100M" CTA.
 *
 * NOTE: real Apple/Google sign-in is not yet built (LoginScreen buttons are
 * "coming soon"), so today the linked branch never fires at runtime and the
 * guest CTA is a TODO — the grant mechanism is ready for when sign-in lands.
 */
function triggerConnectBonus(): void {
  if (localLinked && !localConnectBonusClaimed) {
    networkManager?.claimConnectBonus();
  }
  // else (guest): show the persistent home CTA once the sign-in flow exists.
}

// ── Notification soft-ask + deep-link routing ──────────────────────────────

/** Lazy primer modal. */
let notificationPrimer: NotificationPrimer | null = null;
const NOTIF_PRIMED_KEY = 'sdl_notif_primed';

/**
 * Show the soft-ask priming modal at a value moment (first DM, first bonus), but
 * only once and only when it can actually help: native platform, OS permission
 * still un-asked ('prompt'), and we haven't primed before. If the player taps
 * Enable we fire the real OS prompt; either way we mark it so we never nag.
 */
async function maybePrimeNotifications(): Promise<void> {
  try {
    if (localStorage.getItem(NOTIF_PRIMED_KEY)) return;
    const state = await notificationService.getPermission();
    if (state !== 'prompt') {
      // Already granted (token synced silently) or denied (prompt is spent) —
      // nothing the primer can do. Record it so we stop checking.
      localStorage.setItem(NOTIF_PRIMED_KEY, '1');
      return;
    }
    if (!notificationPrimer) notificationPrimer = new NotificationPrimer();
    const enable = await notificationPrimer.show();
    localStorage.setItem(NOTIF_PRIMED_KEY, '1'); // one-shot regardless of choice
    if (enable) {
      const result = await notificationService.requestPermissionAndRegister();
      if (result === 'denied') {
        showToast('You can turn notifications on later in Settings');
      }
    }
  } catch (err) {
    console.warn('[notif] prime failed:', err);
  }
}

// ── In-app store rating (sentiment funnel) ────────────────────────────────

/** Lazy sentiment-funnel primer. */
let ratingPrimer: RatingPrimer | null = null;
/** Balance at the start of the current game, to detect a net-winning session. */
let preGameStacks = 0;

const RATE_RATED_KEY = 'sdl_rate_rated';     // player went to the store; never re-ask
const RATE_OPTOUT_KEY = 'sdl_rate_optout';   // reserved for an explicit opt-out
const RATE_GAMES_KEY = 'sdl_rate_games';     // games played (engagement proxy)
const RATE_LAST_ASK_KEY = 'sdl_rate_last_ask'; // epoch ms of last primer shown
const RATE_FIRST_SEEN_KEY = 'sdl_rate_first_seen'; // epoch ms of first launch

const RATE_MIN_GAMES = 3;
const RATE_MIN_AGE_MS = 24 * 60 * 60 * 1000;       // 1 day since first launch
const RATE_COOLDOWN_MS = 45 * 24 * 60 * 60 * 1000; // re-ask no sooner than 45 days

/** Record first-launch time once, so the age gate accrues from install. */
function ensureRatingFirstSeen(): void {
  try {
    if (!localStorage.getItem(RATE_FIRST_SEEN_KEY)) {
      localStorage.setItem(RATE_FIRST_SEEN_KEY, String(Date.now()));
    }
  } catch { /* private mode / quota — fine, age gate just won't block */ }
}

/**
 * Ask for a store rating at a peak-positive moment (post-win home return). The
 * native sheet is silent + OS-quota-limited, so the only lever is WHO we ask:
 * happy winners only. A "LOVING IT" tap fires the native sheet; "NOT REALLY"
 * routes to the feedback form (keeping unhappy sentiment out of the store).
 *
 * All gates are local (sdl_rate_* in localStorage), mirroring the notification
 * primer. The funnel UI still runs in the dev browser; only the native review
 * call no-ops off-device.
 */
async function maybeAskForRating(): Promise<void> {
  try {
    // Winning session only — never ask after a loss.
    if (localBalance.stacks <= preGameStacks) return;
    // Already rated or opted out → never nag again.
    if (localStorage.getItem(RATE_RATED_KEY) || localStorage.getItem(RATE_OPTOUT_KEY)) return;
    // Don't collide with the notification primer in the same moment.
    if (document.querySelector('.sdl-nprime-host.visible')) return;
    // Engagement gate.
    const games = Number(localStorage.getItem(RATE_GAMES_KEY) || '0');
    if (games < RATE_MIN_GAMES) return;
    // Age gate — don't ask brand-new installs.
    const firstSeen = Number(localStorage.getItem(RATE_FIRST_SEEN_KEY) || '0');
    if (firstSeen && Date.now() - firstSeen < RATE_MIN_AGE_MS) return;
    // Cooldown — stricter than the OS quota so we never burn an ask on a weak moment.
    const lastAsk = Number(localStorage.getItem(RATE_LAST_ASK_KEY) || '0');
    if (lastAsk && Date.now() - lastAsk < RATE_COOLDOWN_MS) return;

    // Record the ask up front so a crash/close mid-primer still applies cooldown.
    localStorage.setItem(RATE_LAST_ASK_KEY, String(Date.now()));

    if (!ratingPrimer) ratingPrimer = new RatingPrimer();
    const choice = await ratingPrimer.show();
    if (choice === 'love') {
      // Assume they'll rate; the native sheet gives us no result, so don't re-ask.
      localStorage.setItem(RATE_RATED_KEY, '1');
      await ratingService.requestReview();
    } else if (choice === 'meh') {
      // Route unhappy sentiment to feedback, not the store.
      uiManager?.navigateTo('settings');
      settingsScreen?.openTab('support');
    }
    // 'later' → nothing now; the cooldown above re-asks down the line.
  } catch (err) {
    console.warn('[rate] ask failed:', err);
  }
}

/** Route a tapped push (streetdice://...) to the right in-app surface. */
function routePushDeepLink(url: string): void {
  try {
    // streetdice://chat/<id> | streetdice://bonus | streetdice://notifications
    const path = url.replace(/^streetdice:\/\//, '');
    const [head, arg] = path.split('/');
    if (head === 'chat' && arg) {
      uiManager?.navigateTo('crew');
      openStandaloneChat(arg);
    } else if (head === 'notifications') {
      if (notificationsScreen) {
        uiManager?.openModal('notifications', notificationsScreen.element);
        notificationsScreen.onOpen();
      }
    } else {
      // 'bonus' and anything else → home, where the bonus is claimable.
      uiManager?.navigateTo('home');
    }
  } catch (err) {
    console.warn('[notif] deep-link route failed:', url, err);
  }
}

/** Singleton cash-reward presenter (lazy). */
let cashRewardOverlay: CashRewardOverlay | null = null;
/** True while a cash-award animation owns the home Stacks pill (suppresses the
 *  instant balance:update snap so the count-up reads). */
let bonusInFlight = false;

/**
 * Celebrate a balance gain, gated by `lastSeenBalance` so each gain animates
 * exactly once regardless of how many paths report it. Presentation is
 * context-aware:
 *  - On HOME: notes fly from `fromScreenPos` (or screen-centre) to the Stacks
 *    pill, above the home UI, and the pill counts up. No card.
 *  - Elsewhere (e.g. the Store): the player-card cash beat.
 * A non-positive delta just syncs the gate silently (losses don't animate).
 */
async function presentBalanceGain(newBalance: number, fromScreenPos?: { x: number; y: number }): Promise<void> {
  const amount = newBalance - lastSeenBalance;
  if (amount <= 0) { lastSeenBalance = newBalance; return; }
  const startBalance = lastSeenBalance;
  lastSeenBalance = newBalance; // claim immediately — prevents a double-show

  const onHome = uiManager?.getCurrentScreen() === 'home';
  if (!onHome || !homeScreen) {
    await showCashReward(amount, startBalance);
    return;
  }

  const pill = homeScreen.getStacksPillRect();
  const overlay = playerProfilesOverlay;
  // Notes aren't loaded on home by default — fetch real bill art, fall back to
  // the Stacks icon so the flyout is never a silent no-op.
  await fetchCurrencyNotes().catch(() => { /* fall back to the icon */ });
  const noteUrl = getNoteCdnUrl(100_000_000) ?? getNoteCdnUrl(amount) ?? '/assets/icons/currency/stacks.svg';

  bonusInFlight = true;
  // Hold the pill at the pre-gain value during the flyout (overwrites any
  // balance:update snap before paint), then count up when the notes land.
  homeScreen.updatePlayerData({ stacks: startBalance });
  const countUp = () => homeScreen?.animateStacksIncrease(newBalance, 1200, startBalance);

  if (overlay && pill) {
    overlay.setFlyoutAboveUI(true);
    overlay.show();
    const target = { x: pill.left + pill.width / 2, y: pill.top + pill.height / 2 };
    const spawn = fromScreenPos ?? { x: window.innerWidth / 2, y: window.innerHeight * 0.6 };
    // resolveOnComplete: the hide + z-index restore below run on resolve, so
    // we must wait out the +$X label's linger/float-up — otherwise the label
    // finishes its animation at z-index 45, behind the home UI.
    await overlay.flyoutCashWin('', amount, spawn, noteUrl, countUp, { targetOverride: target, skipBalanceCountUp: true, resolveOnComplete: true, labelLiftHeights: 1 });
    overlay.hide();
    overlay.setFlyoutAboveUI(false);
  } else {
    countUp();
    await new Promise((r) => setTimeout(r, 1300));
  }
  bonusInFlight = false;
}

/**
 * Fired whenever the home screen becomes visible (tab nav, game return). If the
 * balance grew while home was away (e.g. game winnings) and hasn't been shown,
 * present the gain on the pill. The gate makes already-shown gains a no-op.
 */
function checkHomeBalanceGain(): Promise<void> {
  if (uiManager?.getCurrentScreen() !== 'home') return Promise.resolve();
  if (localBalance.stacks > lastSeenBalance) {
    return presentBalanceGain(localBalance.stacks);
  }
  return Promise.resolve();
}

/**
 * Global cash-reward beat — player card drops down + in-game note flyout from
 * screen centre + balance count-up. Reusable anywhere (connect bonus, daily
 * bonus, gifts...). `startStacks` defaults to the current local balance; pass
 * the PRE-grant balance when the server has already credited the amount.
 */
function showCashReward(amount: number, startStacks?: number): Promise<void> {
  if (!cashRewardOverlay) cashRewardOverlay = new CashRewardOverlay();
  const overlay = playerProfilesOverlay;
  const noteUrl = getNoteCdnUrl(100_000_000);
  const runFlyout = overlay && noteUrl
    ? async (target: { x: number; y: number }, onLastNoteFlightStart: () => void) => {
        // Overlay defaults to opacity:0; show it for the flyout (no cards set,
        // so only the notes render), then hide it again.
        overlay.show();
        const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        await overlay.flyoutCashWin('', amount, center, noteUrl, onLastNoteFlightStart, {
          targetOverride: target,
          skipBalanceCountUp: true,
          // Same label-vs-hide race as presentBalanceGain: hide() fades the
          // overlay on resolve, so wait for the label's full linger/float-up.
          resolveOnComplete: true,
        });
        overlay.hide();
      }
    : undefined;
  return cashRewardOverlay.show({
    username: localUsername,
    picture: localPicture,
    startStacks: startStacks ?? localBalance.stacks,
    amount,
    noteUrl,
    runFlyout,
  });
}

/**
 * Launch the guided tutorial in an ISOLATED scene (no game-server room). Hides
 * the home UI, shows the 3D view, and hands control to TutorialController. The
 * onExit callback restores the home UI and disposes the instance. Reward dice
 * codes mirror main-server config.game.tutorial.
 */
async function startTutorial(): Promise<void> {
  if (!engine || !networkManager || !uiManager || !gameFlowController) return;
  // Drop any prior instance (its coach/reward overlays live on document.body).
  tutorialController?.destroy();
  tutorialController = null;

  // Cover the scene build with the loading screen (the engine canvas is black
  // until the first frame renders).
  loadingScreen.show();
  uiManager.hide();
  const gameContainer = document.getElementById('game-container');
  if (gameContainer) gameContainer.style.visibility = 'visible';

  // The first two loadout slots are the craps dice (slot 2 is cee-lo). Use the
  // player's equipped red default dice so the tutorial matches their inventory.
  const activePreset = diceScreen?.getActivePreset() ?? 0;
  const slots = diceScreen?.getPresetSlots(activePreset) ?? [];
  const equippedDice = [slots[0], slots[1]].filter(Boolean) as string[];

  tutorialController = new TutorialController({
    engine,
    networkManager,
    gameFlowController,
    playerProfilesOverlay,
    diceAssetManager,
    cdnBase: diceCdnBase,
    equippedDice,
    rewardDice: { shoot: 'ds01-66f7a44e', bet: 'dss01-004157a0' },
    onExit: () => {
      const gc = document.getElementById('game-container');
      if (gc) gc.style.visibility = 'hidden';
      uiManager?.show();
      tutorialController?.destroy();
      tutorialController = null;
      // Surface the 100M connect bonus now that the tutorial's done.
      triggerConnectBonus();
      // Teach where the daily bonus lives while attention is peaked from the
      // tutorial — delayed so home has painted and any reward beat settles.
      window.setTimeout(() => showDailyBonusNudge(), 900);
    },
  });
  await tutorialController.start();
  await loadingScreen.hide();
}

/** Map a room/match ErrorPayload to a short, player-facing line (no dashes). */
function friendlyMatchError(error: { code?: string; message?: string }): string {
  switch (error?.code) {
    case 'ROOM_FULL': return 'That table is full. Try another.';
    case 'ROOM_NOT_FOUND': return 'That table is no longer available.';
    case 'INSUFFICIENT_FUNDS': return 'Not enough Stacks for that table.';
    case 'ALREADY_IN_ROOM': return "You're already at that table.";
    case 'NO_SERVERS': return 'No tables open right now. Try again in a moment.';
    case 'ALLOCATION_MISSING': return 'That table is no longer reachable.';
    case 'CREATE_FAILED': return "Couldn't create the table. Try again.";
    case 'MODE_LOCKED': return 'That mode is not available yet.';
    case 'INVALID_BUY_IN': return 'That buy-in is not valid.';
    case 'BLOCKED': return "You can't join a table with someone you've blocked.";
    case 'NOT_AUTHENTICATED': return 'Please sign in again to play.';
    default: return error?.message || "Couldn't join the table. Try again.";
  }
}

/**
 * Start a game with the given mode
 */
async function startGame(
  mode: 'solo' | 'friends' | 'matchmaking',
  gameType: 'ceelo' | 'craps' = 'ceelo',
): Promise<void> {

  if (!gameStateManager || !uiManager) {
    console.error('[main.ts] Cannot start game - missing gameStateManager or uiManager');
    return;
  }

  // Show loading screen (may already be visible from handleHomeAction)
  loadingScreen.show();

  // Hide main UI behind the loading screen
  uiManager.hide();

  // Show game container (make 3D scene visible behind loading screen)
  const gameContainer = document.getElementById('game-container');
  if (gameContainer) {
    gameContainer.style.visibility = 'visible';
  }

  // Create a promise that resolves when scene setup completes
  const sceneReady = new Promise<void>(resolve => { sceneSetupResolve = resolve; });

  // Start the game — triggers 'game:started' which calls setupGameScene then resolves sceneReady
  gameStateManager.startGame({
    mode,
    gameType,
    diceCount: gameType === 'craps' ? 2 : 3,
  });

  // Wait for both scene to fully load AND minimum display time
  await Promise.all([
    sceneReady,
    loadingScreen.waitForMinTime(),
  ]);

  // Scene is ready — hide loading screen
  await loadingScreen.hide();
}

// Start the game
main().catch(console.error);
